import React, { useMemo, useState, useRef, useEffect, useLayoutEffect } from 'react';
import { DLC_COLORS } from '../../constants/dlcColors';
import './ProgressMap.css';
import { getBossGlyph } from '../../data/bossGlyphs';
import { BossGlyphGroup, bossGlyphTransform } from './BossGlyph';

// Seeded pseudo-random from string hash (deterministic jitter)
function seededRandom(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return ((hash & 0x7fffffff) % 10000) / 10000;
}

// Node-body luminance for reachable/available cards (the opacity fallback below,
// after all locked/unavailable states have been ruled out). Accepts EITHER the
// legacy string enum (peripheral/supporting) OR a numeric 0-1 band — the HTN
// region feeds a numeric line-of-therapy band (HTN_CLASS_LUMINANCE) so first-line
// drugs read brighter than later-line ones. Numeric values are clamped to
// [0.6, 1] so a clinical-rank dim can never sink into the locked-node range
// (0.3-0.55) and read as unavailable. Anything else → full opacity.
function resolveNodeLuminance(importance) {
  if (typeof importance === 'number' && Number.isFinite(importance)) {
    return Math.max(0.6, Math.min(1, importance));
  }
  if (importance === 'peripheral') return 0.4;
  if (importance === 'supporting') return 0.7;
  return 1;
}

// Colors
// Ember — the default warm node color. Pure red (#ef4444, see MINI_BOSS_PENDING) is
// RESERVED for red-node markers (upload-match + mini-boss-pending) and warnings;
// everything that used to default to red now uses OFF_RED so the reserved red reads
// as a distinct signal rather than the ambient base color.
const OFF_RED = '#e06a43';
const OFF_RED_DIM = 'rgba(224, 106, 67, 0.35)';
const PURPLE = '#a78bfa';
const PURPLE_DIM = 'rgba(167, 139, 250, 0.35)';
const GREEN = '#22c55e';
const GREEN_DIM = 'rgba(34, 197, 94, 0.35)';
const MINI_BOSS_PENDING = '#ef4444';
const MINI_BOSS_PENDING_DIM = 'rgba(239, 68, 68, 0.35)';
const MINI_BOSS_PENDING_RING = '#fbbf24';
const BLUE = '#3b82f6';
const BLUE_DIM = 'rgba(59, 130, 246, 0.35)';
const PINK_PURPLE = '#d946ef';
const PINK_PURPLE_DIM = 'rgba(217, 70, 239, 0.35)';
const MANUAL_CYAN = '#06b6d4';
const MANUAL_CYAN_DIM = 'rgba(6, 182, 212, 0.35)';
const YELLOW = '#eab308';
const YELLOW_DIM = 'rgba(234, 179, 8, 0.35)';
const GOLD = '#fbbf24';
const GOLD_DIM = 'rgba(251, 191, 36, 0.2)';
const EARNED_CYAN = '#67e8f9';
const EARNED_CYAN_DIM = 'rgba(103, 232, 249, 0.2)';
// Upload-match indicator: flag a Drug Lab node whose drug was extracted from
// the decks of the exam the user has SELECTED in the exam bar (meta.upload_match,
// stamped server-side and rolled up drug -> class portal -> system hub). Rendered
// as a SINGLE solid target reticle in the node's OWN outline color (see MapNode).
// No tier: a node is either in the selected exam's drug set or it isn't.
// Canonical DLC pill colors — keep map satellites in sync with the
// top-right DLC pill so the map reads as part of the same exam:
//   NAPLEX → green  #22c55e
//   PTCB   → amber  #f59e0b
//   MPJE   → blue   #3b82f6
const NAPLEX_GREEN = '#22c55e';
const PTCB_AMBER = '#f59e0b';
const MPJE_BLUE = '#3b82f6';
// MPJE-only satellite types (statute/compliance/inspector) all live on the
// MPJE map, so they all use the canonical MPJE blue.
const MPJE_STATUTE_VIOLET = MPJE_BLUE;
const MPJE_COMPLIANCE_EMERALD = MPJE_BLUE;
const MPJE_INSPECTOR_BLUE = MPJE_BLUE;
const MPJE_RESERVED_TYPE_COLORS = {
  authority_matrix: '#8b5cf6',
  timeline: '#0ea5e9',
  threshold: '#f59e0b',
  violation_consequence: '#ef4444',
  document_chain: '#14b8a6',
  case_law: '#64748b',
};
const MPJE_RESERVED_NODE_TYPES = new Set(Object.keys(MPJE_RESERVED_TYPE_COLORS));

// Build a wide sine-wave SVG path for animated water surface
function _wavePath(wl, amp, y0, extent) {
  const steps = Math.ceil(extent / (wl / 2));
  let d = `M${-extent / 2},${y0}`;
  for (let i = 0; i < steps; i++) {
    const dir = i % 2 === 0 ? -1 : 1;
    d += ` q${wl / 4},${dir * amp} ${wl / 2},0`;
  }
  d += ` V${y0 + 200} H${-extent / 2} Z`;
  return d;
}

// Portal node colors (death/revive system)
const PORTAL_WHITE = '#e8e4f0';
const PORTAL_WHITE_DIM = 'rgba(232, 228, 240, 0.2)';
const PORTAL_ANCHOR = '#f0eaff';
const PORTAL_ANCHOR_DIM = 'rgba(240, 234, 255, 0.25)';
// HTN "Regimen" portal — a bespoke member of the portal family: it shares the
// off-white teleport chassis (pulse rings + ECG glyph + portal glow) so it reads
// unmistakably as a gateway, but carries the canon §6 warm ember-white tint + an
// off-white ECG glyph + a thin EMBER halo ring so it's tailored to the
// antihypertensive surface ("tailored but meshes"). The glyph is deliberately NOT
// #ef4444 — that token is canon-reserved for warnings / mini-boss-pending markers.
// CRITICAL (round-3 fix): the portal-glow filter is applied to the RINGS+GLYPH
// group, NOT the filled disc. Putting the glow on the disc (round-2 build) bloomed
// the 0.18 fill onto itself (~0.36) and bloomed the stroke into a thick bright rim
// + radial bevel — a "chrome medallion" that all four cohesion lenses flagged P0.
// See wiki/reference/debono-node-visual-canon.md §6 + the 2026-06-30 round-3 critique.
const HTN_PORTAL = '#efe6dd';
// TRANSLUCENT off-white (canon §6.1) — the #0a0a0a void shows through like
// portal_gateway / Psilocybin, so the disc reads as ethereal teleport GLASS, NOT
// an opaque putty/chrome coin (which sat too close to genuinely-locked gray nodes).
// The disc renders flat (NO filter — htn_portal is excluded from the disc-level
// portal-glow); the luminous gateway read comes from the soft-glowing rings + ECG
// glyph group instead. (2026-06-30 cohesion critique round 3.)
const HTN_PORTAL_DIM = 'rgba(239, 230, 221, 0.18)';
// Ember (off-red) — used ONLY as a thin halo ring for the cardio/regimen brand
// cue (canon §6 line 188). The central glyph itself stays off-white in-family;
// #ef4444 stays reserved for warnings/mini-boss.
const HTN_PORTAL_ACCENT = '#e06a43';

// Content-type colors (for diamond prerequisite nodes)
const CONTENT_TYPE_COLORS = {
  mechanism: '#e06a43',     // ember (off-red; pure red reserved for red-node markers)
  recognition: '#0ea5e9',   // sky blue
  pharmacology: '#f59e0b',  // amber
  protocol: '#14b8a6',      // teal
  counseling: '#a78bfa',    // purple
  foundation: '#22c55e',    // green
};

// CRAM node colors
const CRAM_COLORS = {
  cram_portal: '#e06a43',   // ember (enter CRAM) — overridden to blue for back portal
  cram_hub: '#9ca3af',      // gray (overridden by category)
  cram_disease: '#e06a43',  // ember
  cram_treatment: '#f59e0b', // amber
  cram_treatment_group: '#f59e0b', // amber (treatment parent/group header)
  cram_drug: '#3b82f6',     // blue
  cram_deck_portal: '#14b8a6', // teal (territory deck portal)
  cram_exam_center: '#9ca3af', // gray (territory center)
  cram_disease_treatment: '#f97316', // orange (solar system center)
  cram_solar_drug: '#3b82f6', // blue (orbiting drug)
  cram_drill_bank: '#a855f7', // purple (deck-wide drill octagon)
  cram_deck_header: '#6b7280', // gray (deck section header in exam map)
  cram_exam_hub: '#e06a43',   // ember (exam-wide CRAM octagon)
};
const CRAM_BACK_COLOR = '#3b82f6'; // blue (return to Learn)
const CLICKABLE_CRAM_NODE_TYPES = new Set([
  'cram_portal',
  'cram_hub',
  'cram_disease',
  'cram_treatment',
  'cram_treatment_group',
  'cram_drug',
  'cram_deck_portal',
  'cram_exam_center',
  'cram_disease_treatment',
  'cram_solar_drug',
  'cram_drill_bank',
  'cram_exam_hub',
]);

// DLC "all areas" portal.
const DLC_AREAS_PORTAL_COLOR = { naplex: '#fbbf24', ptcb: DLC_COLORS.ptcb.primary };

// Canonical DLC color for shared satellite types (rx_detail / safety /
// clinical_round) based on which DLC map is being rendered.
function dlcSatelliteColor(mapDlc) {
  if (mapDlc === 'naplex') return NAPLEX_GREEN;
  if (mapDlc === 'ptcb') return PTCB_AMBER;
  if (mapDlc === 'mpje') return MPJE_BLUE;
  return null;
}
function dlcSatelliteRgba(mapDlc) {
  if (mapDlc === 'naplex') return 'rgba(34, 197, 94, 0.35)';
  if (mapDlc === 'ptcb') return 'rgba(245, 158, 11, 0.35)';
  if (mapDlc === 'mpje') return 'rgba(59, 130, 246, 0.35)';
  return null;
}

function nodeColor(status, system, type, contentType, synthetic, nodeData, manual, mapDlc) {
  if (type === 'portal_gateway') return PORTAL_WHITE;
  if (type === 'resuscitate') return PORTAL_ANCHOR;
  if (type === 'htn_portal') return HTN_PORTAL;
  if (type === 'dlc_areas_portal') return DLC_AREAS_PORTAL_COLOR[nodeData?.dlc] || '#fbbf24';
  // CRAM node types
  if (type === 'cram_deck_portal') {
    const pct = (nodeData?.coverage_pct || 0) / 100;
    // Interpolate teal (#14b8a6) -> green (#22c55e)
    const r = Math.round(20 + (34 - 20) * pct);
    const g = Math.round(184 + (197 - 184) * pct);
    const b = Math.round(166 + (94 - 166) * pct);
    return `rgb(${r}, ${g}, ${b})`;
  }
  if (type in CRAM_COLORS) {
    if (type === 'cram_portal' && (nodeData?.label === 'Learn' || nodeData?.label === 'Back')) return CRAM_BACK_COLOR;
    if (type === 'cram_hub') {
      const cat = nodeData?.cram_category;
      if (cat === 'disease') return OFF_RED;
      if (cat === 'treatment') return '#f59e0b';
      if (cat === 'drug') return '#3b82f6';
    }
    return CRAM_COLORS[type];
  }
  if (status === 'dead_locked') return '#666';
  // NAPLEX cluster types use their area color
  if (type === 'group_hub' || type === 'slug') return nodeData?.color || '#f97316';
  if (type === 'group_boss' || type === 'area_boss' || type === 'htn_boss') return nodeData?.color || OFF_RED;
  if (type === 'rx_detail') return status === 'completed' ? GREEN : (dlcSatelliteColor(mapDlc) || '#7c3aed');
  if (type === 'safety') return status === 'completed' ? GREEN : (dlcSatelliteColor(mapDlc) || '#059669');
  if (type === 'clinical_round') return status === 'completed' ? GREEN : (dlcSatelliteColor(mapDlc) || '#0891b2');
  if (type === 'statute_detail') return status === 'completed' ? GREEN : MPJE_STATUTE_VIOLET;
  if (type === 'compliance_check') return status === 'completed' ? GREEN : MPJE_COMPLIANCE_EMERALD;
  if (type === 'inspector_walkthrough') return status === 'completed' ? GREEN : MPJE_INSPECTOR_BLUE;
  if (type in MPJE_RESERVED_TYPE_COLORS) return MPJE_RESERVED_TYPE_COLORS[type];
  // Dialysis/drill never show "completed" — always show water state (blue)
  if ((type === 'fountain' || type === 'drill')) {
    if (status === 'passive') return nodeData?.passive_earned ? EARNED_CYAN : GOLD;
    return BLUE;
  }
  if (status === 'completed') return GREEN;
  if (status === 'cleared' && type === 'content') return YELLOW;
  if (type === 'content') return CONTENT_TYPE_COLORS[contentType] || '#f59e0b';
  if (type === 'trial') return manual ? MANUAL_CYAN : synthetic ? PINK_PURPLE : PURPLE;
  // Levels (subtopics) = EMBER off-red default, Routes (objectives) = PURPLE. Pure red is
  // reserved for red-node markers + warnings, so the base map reads ember, not red.
  return system === 'routes' ? (manual ? MANUAL_CYAN : synthetic ? PINK_PURPLE : PURPLE) : OFF_RED;
}
function nodeColorDim(status, system, type, contentType, synthetic, nodeData, manual, mapDlc) {
  if (type === 'portal_gateway') return PORTAL_WHITE_DIM;
  if (type === 'resuscitate') return PORTAL_ANCHOR_DIM;
  if (type === 'htn_portal') return HTN_PORTAL_DIM;
  if (type === 'dlc_areas_portal') {
    const base = DLC_AREAS_PORTAL_COLOR[nodeData?.dlc] || '#fbbf24';
    return base + '33';
  }
  // CRAM dim colors
  if (type === 'cram_deck_portal') {
    // cram_deck_portal returns rgb() from nodeColor — convert to rgba
    const base = nodeColor(status, system, type, contentType, synthetic, nodeData);
    const rgbMatch = base.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    if (rgbMatch) return `rgba(${rgbMatch[1]}, ${rgbMatch[2]}, ${rgbMatch[3]}, 0.25)`;
    return base + '40';
  }
  if (type in CRAM_COLORS) {
    const base = nodeColor(status, system, type, contentType, synthetic, nodeData);
    return base + '40';
  }
  if (status === 'dead_locked') return 'rgba(102, 102, 102, 0.2)';
  // NAPLEX cluster types
  if (type === 'group_hub' || type === 'slug') return (nodeData?.color || '#f97316') + '40';
  if (type === 'group_boss' || type === 'area_boss' || type === 'htn_boss') return (nodeData?.color || OFF_RED) + '55';
  if (type === 'rx_detail') return status === 'completed' ? GREEN_DIM : (dlcSatelliteRgba(mapDlc) || 'rgba(124, 58, 237, 0.35)');
  if (type === 'safety') return status === 'completed' ? GREEN_DIM : (dlcSatelliteRgba(mapDlc) || 'rgba(5, 150, 105, 0.35)');
  if (type === 'clinical_round') return status === 'completed' ? GREEN_DIM : (dlcSatelliteRgba(mapDlc) || 'rgba(8, 145, 178, 0.35)');
  if (type === 'statute_detail') return status === 'completed' ? GREEN_DIM : 'rgba(59, 130, 246, 0.35)';
  if (type === 'compliance_check') return status === 'completed' ? GREEN_DIM : 'rgba(59, 130, 246, 0.35)';
  if (type === 'inspector_walkthrough') return status === 'completed' ? GREEN_DIM : 'rgba(59, 130, 246, 0.35)';
  if (type in MPJE_RESERVED_TYPE_COLORS) return MPJE_RESERVED_TYPE_COLORS[type] + '40';
  if ((type === 'fountain' || type === 'drill')) {
    if (status === 'passive') return nodeData?.passive_earned ? EARNED_CYAN_DIM : GOLD_DIM;
    return BLUE_DIM;
  }
  if (status === 'completed') return GREEN_DIM;
  if (status === 'cleared' && type === 'content') return YELLOW_DIM;
  if (type === 'content') {
    const c = CONTENT_TYPE_COLORS[contentType] || '#f59e0b';
    return c + '59';  // 35% opacity hex suffix
  }
  if (type === 'trial') return manual ? MANUAL_CYAN_DIM : synthetic ? PINK_PURPLE_DIM : PURPLE_DIM;
  return system === 'routes' ? (manual ? MANUAL_CYAN_DIM : synthetic ? PINK_PURPLE_DIM : PURPLE_DIM) : OFF_RED_DIM;
}
function edgeColor(fromStatus, toStatus, fromSystem, edgeType, fromNode, mapDlc) {
  if (edgeType === 'portal_path') return 'rgba(232, 228, 240, 0.3)';
  // CRAM edges
  if (edgeType === 'cram_hub_link') return 'rgba(156, 163, 175, 0.4)';
  if (edgeType === 'cram_cross_ref') return 'rgba(196, 181, 253, 0.3)';
  if (edgeType === 'cram_orbit') return 'rgba(249, 115, 22, 0.2)';
  // NAPLEX cluster edges
  if (edgeType === 'hub_to_slug') return (fromNode?.color || '#f97316') + '55';
  if (edgeType === 'rx_detail') return fromStatus === 'completed' ? GREEN_DIM : (dlcSatelliteRgba(mapDlc) || 'rgba(124, 58, 237, 0.35)');
  if (edgeType === 'safety') return fromStatus === 'completed' ? GREEN_DIM : (dlcSatelliteRgba(mapDlc) || 'rgba(5, 150, 105, 0.35)');
  if (edgeType === 'clinical_round') return fromStatus === 'completed' ? GREEN_DIM : (dlcSatelliteRgba(mapDlc) || 'rgba(8, 145, 178, 0.35)');
  if (edgeType === 'statute_detail') return fromStatus === 'completed' ? GREEN_DIM : 'rgba(59, 130, 246, 0.35)';
  if (edgeType === 'compliance_check') return fromStatus === 'completed' ? GREEN_DIM : 'rgba(59, 130, 246, 0.35)';
  if (edgeType === 'inspector_walkthrough') return fromStatus === 'completed' ? GREEN_DIM : 'rgba(59, 130, 246, 0.35)';
  if (edgeType === 'slug_to_boss') return fromStatus === 'completed' ? GREEN_DIM : 'rgba(255,255,255,0.12)';
  if (edgeType === 'boss_to_area') return fromStatus === 'completed' ? GREEN_DIM : 'rgba(255,255,255,0.08)';
  if (fromStatus === 'completed' && toStatus === 'completed') return GREEN;
  if (edgeType === 'content_path') return '#f59e0b55';
  if (edgeType === 'reward_path') return BLUE_DIM;
  if (edgeType === 'drill_path') return BLUE_DIM;
  return fromSystem === 'routes' ? PURPLE_DIM : OFF_RED_DIM;
}

// Node sizes by type
const NODE_R = { topic: 14, shared: 11, boss: 20, final_boss: 26, drill: 12, trial: 14, content: 12, fountain: 12, portal_gateway: 16, resuscitate: 18, therapeutic_area: 18, group_hub: 22, slug: 14, group_boss: 20, area_boss: 26, synthesis: 11, tier_boss: 16, apothecarius: 30, clinical_round: 18, rx_detail: 11, safety: 16, statute_detail: 11, compliance_check: 16, inspector_walkthrough: 18, authority_matrix: 14, timeline: 14, threshold: 14, violation_consequence: 14, document_chain: 14, case_law: 14, cram_portal: 20, cram_hub: 28, cram_disease: 14, cram_treatment: 14, cram_treatment_group: 18, cram_drug: 12, cram_deck_portal: 36, cram_exam_center: 30, cram_disease_treatment: 22, cram_solar_drug: 12, cram_drill_bank: 22, cram_deck_header: 14, cram_exam_hub: 52, dlc_areas_portal: 22, keystone: 20, htn_portal: 18, htn_boss: 26 };

// CRAM node types that keep their full size (octagons / hub elements)
const CRAM_FULL_SIZE_TYPES = new Set(['cram_exam_hub', 'cram_drill_bank', 'cram_exam_center', 'cram_deck_portal']);

// Compute dynamic scale factor for non-hub CRAM nodes based on total node count.
// More nodes → smaller satellites, keeping the map balanced.
// Returns 1.0 for <=15 nodes, scales down to 0.45 for very dense maps (60+ nodes).
function cramNodeScale(totalNodes) {
  if (totalNodes <= 15) return 1.0;
  if (totalNodes >= 60) return 0.45;
  // Linear interpolation: 15→1.0, 60→0.45
  return 1.0 - ((totalNodes - 15) / (60 - 15)) * 0.55;
}

// Layout: viewBox = container size (1:1 pixel mapping, no SVG scaling).
// Nodes scatter along a wandering diagonal from top-left to bottom-right.
// PAD_T / PAD_B are sized to clear dashboard trays and top pill nav that
// overlay the map viewport. Container size itself is unchanged.
const PAD_L = 70, PAD_R = 60, PAD_T = 90, PAD_B = 150;

function computePositions(nodes, gridCols, gridRows, containerW, containerH) {
  const N = nodes.length;
  if (N === 0) return { positions: {}, totalW: containerW, totalH: containerH };

  const positions = {};

  // Usable area
  const x0 = PAD_L, y0 = PAD_T;
  const x1 = containerW - PAD_R, y1 = containerH - PAD_B;
  const areaW = x1 - x0, areaH = y1 - y0;

  // Pin _bottomRight nodes to bottom-right corner (same as radial layout)
  const pinnedNodes = nodes.filter(n => n._bottomRight);
  const pathNodes = nodes.filter(n => !n._bottomRight);
  pinnedNodes.forEach(n => {
    positions[n.id] = { x: containerW - 60, y: containerH - PAD_B - 30 };
  });

  const M = pathNodes.length;

  // Diagonal direction and perpendicular
  const diagLen = Math.sqrt(areaW * areaW + areaH * areaH) || 1;
  const dirX = areaW / diagLen, dirY = areaH / diagLen;
  const perpX = -dirY, perpY = dirX;

  // Wander: random walk so consecutive nodes meander smoothly
  const wanderAmp = Math.min(areaW, areaH) * 0.22;
  const wanderStep = wanderAmp * 0.55; // max change per node — keeps path smooth
  const segmentLen = M > 1 ? diagLen / (M - 1) : 0;
  const alongAmp = segmentLen * 0.15;

  // Build smooth wander offsets via random walk (seeded per node)
  let currentWander = 0;
  const wanderOffsets = [];
  for (let i = 0; i < M; i++) {
    const delta = (seededRandom(pathNodes[i].id + 'wd') - 0.5) * 2 * wanderStep;
    currentWander += delta;
    // Soft pull toward center to prevent path from hugging one edge
    currentWander *= 0.92;
    currentWander = Math.max(-wanderAmp, Math.min(wanderAmp, currentWander));
    wanderOffsets.push(currentWander);
  }

  for (let i = 0; i < M; i++) {
    const node = pathNodes[i];
    const t = M > 1 ? i / (M - 1) : 0.5;

    // Base position along the main diagonal
    const baseX = x0 + t * areaW;
    const baseY = y0 + t * areaH;

    // Smooth perpendicular wander from random walk
    const wander = wanderOffsets[i];
    // Along-path jitter (seeded per node, independent is fine here)
    const along = (seededRandom(node.id + 'along') - 0.5) * 2 * alongAmp;

    const px = baseX + perpX * wander + dirX * along;
    const py = baseY + perpY * wander + dirY * along;

    positions[node.id] = {
      x: Math.max(x0, Math.min(x1, px)),
      y: Math.max(y0, Math.min(y1, py)),
    };
  }

  return { positions, totalW: containerW, totalH: containerH };
}

// Organic bezier curve with perpendicular bulge so even same-row edges arc
function curvePath(x1, y1, x2, y2, seed) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  // Perpendicular unit vector
  const px = -dy / dist;
  const py = dx / dist;
  // Seeded bulge: alternates direction and varies magnitude for organic feel
  const bulgeSign = (seed || 0) % 2 === 0 ? 1 : -1;
  const bulgeAmount = dist * (0.12 + (seed || 0) % 3 * 0.04) * bulgeSign;
  const cx1 = x1 + dx * 0.35 + px * bulgeAmount;
  const cy1 = y1 + dy * 0.35 + py * bulgeAmount;
  const cx2 = x2 - dx * 0.35 + px * bulgeAmount * 0.6;
  const cy2 = y2 - dy * 0.35 + py * bulgeAmount * 0.6;
  return `M${x1},${y1} C${cx1},${cy1} ${cx2},${cy2} ${x2},${y2}`;
}

export function isMapNodeClickable(node) {
  const status = node?.status || 'locked';
  const isUnavailable = node?.has_content === false;
  const isPortal = node?.type === 'portal_gateway' || node?.type === 'resuscitate' || node?.type === 'htn_portal';
  const isHub = node?.type === 'group_hub';
  const isOverviewHub = isHub && node?.system === 'naplex_overview';
  const isLocked = status === 'locked' || status === 'dead_locked' || isUnavailable;

  if (!node?.type || node.type === 'cram_deck_header' || MPJE_RESERVED_NODE_TYPES.has(node.type)) return false;
  if (CLICKABLE_CRAM_NODE_TYPES.has(node.type)) return !isUnavailable && (isPortal || !isLocked);

  return (isOverviewHub || !isHub) && !isUnavailable && (isPortal || !isLocked);
}

function MapEdge({ edge, positions, nodesById, mapDlc }) {
  // Skip orbit edges — animated nodes make static edges wrong; orbit tracks suffice
  if (edge.type === 'cram_orbit') return null;

  const from = nodesById[edge.from];
  const to = nodesById[edge.to];
  if (!from || !to) return null;

  const p1 = positions[edge.from];
  const p2 = positions[edge.to];
  if (!p1 || !p2) return null;

  const color = edgeColor(from.status, to.status, from.system, edge.type, from, mapDlc);
  const isBridge = edge.type === 'bridge';
  const isBranch = edge.type === 'branch';
  const isDrillPath = edge.type === 'drill_path';
  const isContentPath = edge.type === 'content_path';
  const isRewardPath = edge.type === 'reward_path';
  const isPortalPath = edge.type === 'portal_path';
  const isHubToSlug = edge.type === 'hub_to_slug';
  const isSlugToBoss = edge.type === 'slug_to_boss';
  const isBossToArea = edge.type === 'boss_to_area';
  const isSynthesis = edge.type === 'synthesis';
  const isTier = edge.type === 'tier';
  const isClinicalRound = edge.type === 'clinical_round';
  const isRxDetail = edge.type === 'rx_detail';
  const isSafetyEdge = edge.type === 'safety';
  const isInspectorWalkthrough = edge.type === 'inspector_walkthrough';
  const isStatuteDetail = edge.type === 'statute_detail';
  const isComplianceCheck = edge.type === 'compliance_check';
  const isCramHubLink = edge.type === 'cram_hub_link';
  const isCramCrossRef = edge.type === 'cram_cross_ref';
  // Drug-Lab constellation cross-link: colored by relationship kind (carries its
  // own edge.color), solid + slightly brighter so it reads as a meaningful line.
  const isRelation = edge.type === 'relation';
  const isClusterEdge = isHubToSlug || isSlugToBoss || isBossToArea;

  // Synthesis, tier, rx_detail, and safety edges use straight lines to avoid confusing sweeping curves
  const useStraightLine = isSynthesis || isTier || isClinicalRound || isRxDetail || isSafetyEdge || isInspectorWalkthrough || isStatuteDetail || isComplianceCheck || isCramHubLink;
  const pathD = useStraightLine
    ? `M${p1.x},${p1.y} L${p2.x},${p2.y}`
    : curvePath(p1.x, p1.y, p2.x, p2.y, edge._idx);

  return (
    <path
      d={pathD}
      fill="none"
      stroke={isRelation ? (edge.color || 'rgba(232, 228, 240, 0.55)') : isCramHubLink ? color : isCramCrossRef ? 'rgba(196, 181, 253, 0.4)' : isClusterEdge ? color : isRxDetail || isStatuteDetail ? '#3b82f688' : isSafetyEdge || isComplianceCheck ? '#3b82f688' : isClinicalRound || isInspectorWalkthrough ? '#3b82f688' : isSynthesis ? '#d9770688' : isTier ? '#b91c1c88' : isPortalPath ? 'rgba(232, 228, 240, 0.35)' : isContentPath ? (CONTENT_TYPE_COLORS[from.content_type] || '#f59e0b') + '88' : isDrillPath ? BLUE_DIM : isRewardPath ? BLUE_DIM : isBranch ? PURPLE_DIM : color}
      strokeWidth={isRelation ? 1.6 : isCramHubLink ? 1.2 : isCramCrossRef ? 1 : isClusterEdge ? (isHubToSlug ? 1 : isSlugToBoss ? 1 : 1.5) : isRxDetail || isStatuteDetail ? 1 : isSafetyEdge || isComplianceCheck ? 1.2 : isClinicalRound || isInspectorWalkthrough ? 1.2 : isSynthesis ? 1 : isTier ? 1.5 : isPortalPath ? 1 : isBridge ? 1.2 : isContentPath ? 1.5 : isDrillPath ? 1.2 : isRewardPath ? 1.2 : isBranch ? 1 : 1.5}
      strokeDasharray={isRelation ? 'none' : isCramHubLink ? 'none' : isCramCrossRef ? '3 2' : isRxDetail || isStatuteDetail ? '2 3' : isSafetyEdge || isComplianceCheck ? '3 2' : isClinicalRound || isInspectorWalkthrough ? '4 3' : isSynthesis ? '3 2' : isTier ? '5 3' : isSlugToBoss ? '4 3' : isBossToArea ? '6 4' : isPortalPath ? '4 4' : isBridge ? '4 3' : isDrillPath ? '3 2' : isContentPath ? 'none' : isRewardPath ? '3 2' : isBranch ? '3 2' : 'none'}
      strokeLinecap="round"
      opacity={from.status === 'locked' && to.status === 'locked' ? 0.2 : isRelation ? 0.55 : isCramHubLink ? 0.6 : isCramCrossRef ? 0.4 : isPortalPath ? 0.5 : isClusterEdge ? 0.5 : isRxDetail || isStatuteDetail ? 0.5 : isSafetyEdge || isComplianceCheck ? 0.5 : isClinicalRound || isInspectorWalkthrough ? 0.5 : isSynthesis ? 0.5 : isTier ? 0.6 : 0.7}
    />
  );
}

// Drug Lab 'All Systems' compass: a system's six nodes live inside a DISC whose
// radius is capped by the cell height (~90px), and six nodes at the normal map
// scale do not fit — measured, the declutter pass ejected 14 of 48 circles past
// their own rim. So hub nodes are drawn a size down. This is the compass, a
// preview of eight systems; the full-size nodes are one d-pad press away.
// `heading` is the 'All Systems' compass mark. It was 13 when a heading was a
// labelled dot at the centre of a disc of five preview classes; it is now the only
// thing in its slot and carries that system's organ, so it is sized to read as the
// system rather than as a node. `cls` is retained for the preview classes, which
// the Drug Lab no longer emits.
const HUB_NODE_R = { heading: 32, cls: 9 };
function _hubNodeR(node) {
  const m = node.meta || {};
  if (m.hub_heading) return HUB_NODE_R.heading;
  if (typeof m.hub_cluster === 'number') return HUB_NODE_R.cls;
  return null;
}

function MapNode({ node, pos, onNodeClick, onNodeHover, hovered, onHover, cramScale = 1, glowNodeTypes, mapDlc, activeNodeId, forceClickable = false }) {
  const baseR = _hubNodeR(node) ?? NODE_R[node.type] ?? 14;
  // Apply dynamic scale to non-hub CRAM nodes (planets + drugs get smaller on dense maps)
  const isCramScalable = node.type?.startsWith('cram_') && !CRAM_FULL_SIZE_TYPES.has(node.type);
  // Content-volume scaling for planets: more source slides → larger node
  // Maps slide count 3-30 → scale 0.7-1.3 (clamped)
  const contentScale = node.type === 'cram_disease_treatment' && node.source_slide_count
    ? Math.max(0.7, Math.min(1.3, 0.7 + ((node.source_slide_count - 3) / 27) * 0.6))
    : 1.0;
  const r = isCramScalable ? Math.max(4, Math.round(baseR * cramScale * contentScale)) : baseR;
  const status = node.status || 'locked';
  const isPortal = node.type === 'portal_gateway' || node.type === 'resuscitate' || node.type === 'htn_portal';
  // Boss nodes use their creature color; others use status-based red/green
  const isBossType = node.type === 'boss' || node.type === 'final_boss' || node.type === 'group_boss' || node.type === 'area_boss' || node.type === 'tier_boss' || node.type === 'apothecarius' || node.type === 'htn_boss';
  const isAreaType = node.type === 'therapeutic_area';
  const isClusterType = node.type === 'group_hub' || node.type === 'slug' || node.type === 'group_boss' || node.type === 'area_boss' || node.type === 'synthesis' || node.type === 'tier_boss' || node.type === 'apothecarius' || node.type === 'clinical_round' || node.type === 'rx_detail' || node.type === 'safety' || node.type === 'statute_detail' || node.type === 'compliance_check' || node.type === 'inspector_walkthrough' || MPJE_RESERVED_NODE_TYPES.has(node.type);
  const usesOwnColor = ((isBossType || isAreaType || isClusterType) || node.type === 'topic' || node.type === 'keystone') && node.color;
  const sys = node.system || 'routes';
  const contentType = node.content_type || '';
  const isSynthetic = !!node.synthetic;
  const isManual = !!node.manual;
  const isUnavailable = node.has_content === false;
  const isFountainType = node.type === 'fountain' || node.type === 'drill';
  const isCramCovered = status === 'covered' && (node.type === 'cram_disease' || node.type === 'cram_treatment' || node.type === 'cram_treatment_group' || node.type === 'cram_drug' || node.type === 'cram_disease_treatment' || node.type === 'cram_solar_drug');
  const miniBossPending = Boolean(node.mini_boss_pending || node.miniBossPending);
  // Upload-matched nodes render as canonical RED NODES — same pure-red body + animated
  // gold pulse ring as a mini-boss-pending node. `isRedNode` drives the reserved red
  // body color and the shared red-node indicator block below (replaces the old crosshair).
  const isRedNode = miniBossPending || Boolean(node.meta?.upload_match);
  const color = isRedNode ? MINI_BOSS_PENDING : isCramCovered ? YELLOW : status === 'completed' && node.type !== 'group_hub' && !isFountainType && !node.keepAccent ? GREEN : usesOwnColor ? node.color : nodeColor(status, sys, node.type, contentType, isSynthetic, node, isManual, mapDlc);
  const dimColor = isRedNode ? MINI_BOSS_PENDING_DIM : isCramCovered ? YELLOW_DIM : status === 'completed' && node.type !== 'group_hub' && !isFountainType && !node.keepAccent ? GREEN_DIM : usesOwnColor ? `${node.color}55` : nodeColorDim(status, sys, node.type, contentType, isSynthetic, node, isManual, mapDlc);
  const isLocked = status === 'locked' || status === 'dead_locked' || isUnavailable;
  const isCramItem = node.type === 'cram_disease' || node.type === 'cram_treatment' || node.type === 'cram_treatment_group' || node.type === 'cram_drug';
  const isCramDeckPortal = node.type === 'cram_deck_portal';
  const isCramExamCenter = node.type === 'cram_exam_center';
  const isCramDrillBank = node.type === 'cram_drill_bank';
  const isCramDeckHeader = node.type === 'cram_deck_header';
  const isCramExamHub = node.type === 'cram_exam_hub';
  const isCramPortal = node.type === 'cram_portal';
  const isClickable = forceClickable ? !!node?.type : isMapNodeClickable(node);
  const hasStableCramHitTarget =
    isClickable &&
    node.type?.startsWith('cram_') &&
    !isCramPortal &&
    !isCramDeckPortal &&
    !isCramExamCenter &&
    !isCramExamHub &&
    !isCramDrillBank &&
    !isCramDeckHeader;
  const isHovered = hovered === node.id;
  const classMemberCount = Number(node.meta?.member_count ?? 0);
  const classCompletedCount = Number(node.meta?.completed_count ?? 0);
  const showClassCompletionCounter =
    node.node_role === 'graph:class' &&
    node.type === 'topic' &&
    status !== 'completed' &&
    Number.isFinite(classMemberCount) &&
    Number.isFinite(classCompletedCount) &&
    classMemberCount > 0 &&
    classCompletedCount > 0 &&
    classCompletedCount < classMemberCount;

  // Mixed-type final boss: two distinct class colors
  const isMixed = node.type === 'final_boss' && node.colors
    && node.colors.length === 2 && node.colors[0] !== node.colors[1];
  const color2 = isMixed ? node.colors[1] : null;
  const gradId = isMixed ? `grad-${node.id}` : null;
  // Bespoke per-enemy boss hieroglyph (a combat-dynamic mark, see bossGlyphs.jsx).
  // Null when the boss node carries no roster enemy → the per-type glyphs below
  // (crown/skull/swords) render as the fallback.
  const bossGlyph = (isBossType && status !== 'completed') ? getBossGlyph(node) : null;

  const handleClick = (e) => {
    if (!isClickable) return;
    onNodeClick?.(node, e);
  };

  const scale = isHovered && isClickable ? 1.15 : 1;
  const glowR = isHovered && isClickable ? r + 8 : 0;

  const isActive = activeNodeId && activeNodeId === node.id;

  // Upload-match treatment: a node whose drug was extracted from the decks of
  // the exam selected in the exam bar (meta.upload_match, rolled up drug ->
  // class -> hub). Rendered as a single subtle target reticle in the node's OWN
  // outline color (stroke={color}, like the skill-tree glyphs) — NO glow, NO
  // fill, NO badge, NO tier. See the reticle block lower down.
  const uploadMatch = node.meta?.upload_match;

  return (
    <g
      data-node-id={node.id}
      data-testid={uploadMatch ? `upload-match-${uploadMatch}` : undefined}
      transform={`translate(${pos.x},${pos.y}) scale(${scale})`}
      style={{
        cursor: isUnavailable ? 'not-allowed' : isClickable ? 'pointer' : 'default',
        transition: 'transform 0.15s ease, opacity 0.2s ease',
        filter: status === 'dead_locked' ? 'saturate(0.15)' : undefined,
      }}
      onClick={handleClick}
      onMouseEnter={(e) => { onHover(node.id); if (isClickable) onNodeHover?.(node, e); }}
      onMouseLeave={(e) => { onHover(null); if (isClickable) onNodeHover?.(null, e); }}
      opacity={isCramItem && status !== 'completed' ? 0.3 : isUnavailable ? 0.35 : status === 'dead_locked' ? 0.4 : isLocked ? ((node.type === 'topic' || node.type === 'keystone') ? 0.55 : 0.3) : resolveNodeLuminance(node.importance)}
    >

      {/* Active-node indicator — outer ring at r+5 in the node's own
          identity color. Signals "this node's detail is open; click again
          to close." Consistent across all node variants (owned green,
          pursuable gold, keystone red) since it inherits the stroke color
          from each variant. */}
      {isActive && (
        <circle
          r={r + 5}
          fill="none"
          stroke={isMixed ? `url(#${gradId})` : color}
          strokeWidth={1.2}
          strokeOpacity={0.75}
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* Guided first-equip gate — the whitelisted axis reps (one per HTN axis)
          get a bright axis-colored highlight ring (crisp inner + soft outer halo)
          so the four lit, selectable options read as "your choices" against the
          dimmed + padlocked rest of the flower. Set upstream via node.isStarterDrug
          in skillTreeToMapData; absent on every normal node. */}
      {node.isStarterDrug && (
        <>
          <circle
            r={r + 6}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeOpacity={0.95}
            style={{ pointerEvents: 'none' }}
          />
          <circle
            r={r + 10}
            fill="none"
            stroke={color}
            strokeWidth={1}
            strokeOpacity={0.35}
            style={{ pointerEvents: 'none' }}
          />
        </>
      )}

      {/* Gradient def for mixed-type final bosses */}
      {isMixed && (
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={color} />
            <stop offset="100%" stopColor={color2} />
          </linearGradient>
        </defs>
      )}

      {/* Glow on hover */}
      {glowR > 0 && (
        <circle r={glowR} fill="none" stroke={isMixed ? `url(#${gradId})` : color} strokeWidth={1} opacity={0.3} />
      )}

      {/* Discovery hint pulse glow — shown when this node type has a pending hint.
          Completed/cleared nodes never pulse (Edward 2026-05-21: green-checkmarked
          nodes should never have the outside glow). */}
      {(() => {
        const isCompletedShape = status === 'completed' || status === 'cleared';
        const shouldGlow = glowNodeTypes?.has(node.type) && isClickable && !isLocked && !isCompletedShape;
        if (!shouldGlow) return null;
        return (
          <circle
            cx={0} cy={0}
            r={r + 4}
            fill="none"
            stroke={color}
            strokeWidth={1.5}
            opacity={0}
          >
            <animate attributeName="r" values={`${r+3};${r+10};${r+3}`} dur="2.2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0;0.45;0" dur="2.2s" repeatCount="indefinite" />
          </circle>
        );
      })()}

      {/* Outer ring for boss/final/group_boss/area_boss/tier_boss/apothecarius/htn_boss */}
      {(node.type === 'boss' || node.type === 'final_boss' || node.type === 'group_boss' || node.type === 'area_boss' || node.type === 'tier_boss' || node.type === 'apothecarius' || node.type === 'htn_boss') && (
        <circle r={r + 3} fill="none" stroke={isMixed ? `url(#${gradId})` : color} strokeWidth={node.type === 'apothecarius' ? 1.5 : 1} opacity={0.4} />
      )}
      {/* Clinical round — dashed outer ring */}
      {(node.type === 'clinical_round' || node.type === 'inspector_walkthrough') && (
        <circle r={r + 3} fill="none" stroke={color} strokeWidth={1} strokeDasharray="3 2" opacity={0.5} />
      )}
      {node.type === 'apothecarius' && (
        <circle r={r + 6} fill="none" stroke={color} strokeWidth={0.6} opacity={0.2} />
      )}

      {/* Curated release-diamond affordance — a dashed accent ring + faint halo
          marks the knowledge diamonds that BANK a concept card on click (HTN /play
          demo, node.releasable). STATIC by design (no SMIL) to avoid the map-repaint
          jank seen previously on this surface. It disappears once the diamond is
          collected (status flips to 'completed'/green) or while still locked. */}
      {node.releasable && status === 'available' && (
        <>
          <circle r={r + 4} fill="none" stroke={color} strokeWidth={1.4} strokeDasharray="3 2.5" opacity={0.9} />
          <circle r={r + 7.5} fill="none" stroke={color} strokeWidth={0.8} opacity={0.3} />
        </>
      )}

      {/* Diamond shape for content nodes — preserved at every status; the
          yellow-with-check overlay below sits on top when completed/cleared. */}
      {node.type === 'content' && (
        <rect
          x={-r * 0.7}
          y={-r * 0.7}
          width={r * 1.4}
          height={r * 1.4}
          transform="rotate(45)"
          fill={dimColor}
          stroke={color}
          strokeWidth={1.5}
          rx={1}
        />
      )}

      {/* CRAM hub — rotating hexagon with glow */}
      {node.type === 'cram_hub' && (() => {
        const hexPts = [];
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i - Math.PI / 6;
          hexPts.push(`${Math.cos(angle) * r},${Math.sin(angle) * r}`);
        }
        const outerPts = [];
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i - Math.PI / 6;
          outerPts.push(`${Math.cos(angle) * (r + 4)},${Math.sin(angle) * (r + 4)}`);
        }
        return (
          <>
            {/* Outer ring — slow counter-rotate */}
            <polygon points={outerPts.join(' ')} fill="none" stroke={color} strokeWidth={0.6} opacity={0.2}>
              <animateTransform attributeName="transform" type="rotate" values="0;-360" dur="20s" repeatCount="indefinite" />
            </polygon>
            {/* Main hexagon — slow rotate */}
            <polygon points={hexPts.join(' ')} fill={dimColor} stroke={color} strokeWidth={2}>
              <animateTransform attributeName="transform" type="rotate" values="0;360" dur="30s" repeatCount="indefinite" />
            </polygon>
            {/* Center glow pulse — brighter when hub_elo is high */}
            {(() => {
              const hubGlowBase = node.hub_elo && node.hub_elo > 1000 ? '0.12;0.30;0.12' : '0.06;0.18;0.06';
              return (
                <circle r={r * 0.55} fill={color} opacity={0.08}>
                  <animate attributeName="opacity" values={hubGlowBase} dur="3s" repeatCount="indefinite" />
                  <animate attributeName="r" values={`${r * 0.5};${r * 0.65};${r * 0.5}`} dur="3s" repeatCount="indefinite" />
                </circle>
              );
            })()}
          </>
        );
      })()}

      {/* CRAM drill bank — rotating purple octagon */}
      {node.type === 'cram_drill_bank' && (() => {
        const octPts = [];
        for (let i = 0; i < 8; i++) {
          const angle = (Math.PI / 4) * i - Math.PI / 8;
          octPts.push(`${Math.cos(angle) * r},${Math.sin(angle) * r}`);
        }
        const outerPts = [];
        for (let i = 0; i < 8; i++) {
          const angle = (Math.PI / 4) * i - Math.PI / 8;
          outerPts.push(`${Math.cos(angle) * (r + 3)},${Math.sin(angle) * (r + 3)}`);
        }
        return (
          <>
            {/* Outer ring — slow counter-rotate */}
            <polygon points={outerPts.join(' ')} fill="none" stroke="#a855f7" strokeWidth={0.6} opacity={0.25}>
              <animateTransform attributeName="transform" type="rotate" values="0;-360" dur="18s" repeatCount="indefinite" />
            </polygon>
            {/* Main octagon — slow rotate */}
            <polygon points={octPts.join(' ')} fill="rgba(168,85,247,0.12)" stroke="#a855f7" strokeWidth={1.8}>
              <animateTransform attributeName="transform" type="rotate" values="0;360" dur="24s" repeatCount="indefinite" />
            </polygon>
            {/* Center glow pulse */}
            <circle r={r * 0.45} fill="#a855f7" opacity={0.08}>
              <animate attributeName="opacity" values="0.06;0.2;0.06" dur="3s" repeatCount="indefinite" />
              <animate attributeName="r" values={`${r * 0.4};${r * 0.55};${r * 0.4}`} dur="3s" repeatCount="indefinite" />
            </circle>
            {/* Stable click target over rotating octagon */}
            <circle r={r * 1.2} fill="transparent" style={{ cursor: 'pointer' }} />
          </>
        );
      })()}

      {/* CRAM disease — diamond with gentle float */}
      {node.type === 'cram_disease' && (
        <g>
          <animateTransform attributeName="transform" type="translate" values="0,0;0,-1.5;0,0" dur="3s" repeatCount="indefinite" />
          <rect
            x={-r * 0.7} y={-r * 0.7}
            width={r * 1.4} height={r * 1.4}
            transform="rotate(45)"
            fill={dimColor} stroke={color} strokeWidth={1.5} rx={1}
          />
        </g>
      )}

      {/* CRAM treatment — rounded square with gentle float */}
      {node.type === 'cram_treatment' && (
        <g>
          <animateTransform attributeName="transform" type="translate" values="0,0;0,-1.5;0,0" dur="3.5s" repeatCount="indefinite" />
          <rect
            x={-r * 0.8} y={-r * 0.8}
            width={r * 1.6} height={r * 1.6}
            fill={dimColor} stroke={color} strokeWidth={1.5} rx={4}
          />
        </g>
      )}

      {/* CRAM treatment group — hexagonal group header (amber) */}
      {node.type === 'cram_treatment_group' && (() => {
        const hexPts = [];
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i - Math.PI / 6;
          hexPts.push(`${Math.cos(angle) * r},${Math.sin(angle) * r}`);
        }
        return (
          <g>
            <animateTransform attributeName="transform" type="translate" values="0,0;0,-1;0,0" dur="4s" repeatCount="indefinite" />
            <polygon points={hexPts.join(' ')} fill={dimColor} stroke={color} strokeWidth={1.5} />
          </g>
        );
      })()}

      {/* CRAM deck portal — rounded card with category dots (territory map) */}
      {node.type === 'cram_deck_portal' && (() => {
        const w = 110, h = 52;
        const counts = node.counts || { disease: 0, treatment: 0, drug: 0 };
        const teal = '#14b8a6';
        const tealDim = 'rgba(20, 184, 166, 0.15)';
        const cats = [
          { key: 'disease', color: '#e06a43', count: counts.disease },
          { key: 'treatment', color: '#f59e0b', count: counts.treatment },
          { key: 'drug', color: '#3b82f6', count: counts.drug },
        ].filter(c => c.count > 0);
        const dotStartX = -(cats.length * 18) / 2 + 9;
        return (
          <g>
            <animateTransform attributeName="transform" type="translate" values="0,0;0,-1;0,0" dur="4s" repeatCount="indefinite" />
            {/* Outer glow */}
            <rect x={-w/2 - 2} y={-h/2 - 2} width={w + 4} height={h + 4} rx={12}
              fill="none" stroke={teal} strokeWidth={1} opacity={0.3}>
              <animate attributeName="opacity" values="0.15;0.35;0.15" dur="3s" repeatCount="indefinite" />
            </rect>
            {/* Card body */}
            <rect x={-w/2} y={-h/2} width={w} height={h} rx={10}
              fill={tealDim} stroke={teal} strokeWidth={1.5} />
            {/* Deck name */}
            <text textAnchor="middle" y={-8}
              fill="#e2e8f0" fontSize={9} fontWeight={600}
              fontFamily="var(--font-primary, sans-serif)">
              {splitLabel(node.short_label || node.label, 18)[0]}
            </text>
            {splitLabel(node.short_label || node.label, 18)[1] && (
              <text textAnchor="middle" y={2}
                fill="#e2e8f0" fontSize={8} fontWeight={500} opacity={0.8}
                fontFamily="var(--font-primary, sans-serif)">
                {splitLabel(node.short_label || node.label, 18)[1]}
              </text>
            )}
            {/* Category dots with counts */}
            {cats.map((c, ci) => (
              <g key={c.key} transform={`translate(${dotStartX + ci * 18}, ${splitLabel(node.short_label || node.label, 18)[1] ? 16 : 12})`}>
                <circle r={4} fill={c.color} opacity={0.7} />
                <text x={0} y={-6} textAnchor="middle" fill={c.color} fontSize={7} fontWeight={600}
                  fontFamily="var(--font-primary, sans-serif)">
                  {c.count}
                </text>
              </g>
            ))}
          </g>
        );
      })()}

      {/* CRAM exam center — octagon with exam label (territory map) */}
      {node.type === 'cram_exam_center' && (() => {
        const s = r * 1.1;
        const pts = [];
        for (let i = 0; i < 8; i++) {
          const angle = (Math.PI / 4) * i - Math.PI / 8;
          pts.push(`${s * Math.cos(angle)},${s * Math.sin(angle)}`);
        }
        return (
          <g>
            <polygon points={pts.join(' ')}
              fill="rgba(156, 163, 175, 0.12)" stroke="#9ca3af" strokeWidth={1.5} />
            <polygon points={pts.join(' ')}
              fill="none" stroke="#9ca3af" strokeWidth={0.5} opacity={0.4}>
              <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="60s" repeatCount="indefinite" />
            </polygon>
            <text textAnchor="middle" y={-2}
              fill="#d1d5db" fontSize={7} fontWeight={700} letterSpacing="0.5"
              fontFamily="var(--font-primary, sans-serif)">
              CRAM
            </text>
            <text textAnchor="middle" y={8}
              fill="#9ca3af" fontSize={6} fontWeight={500}
              fontFamily="var(--font-primary, sans-serif)">
              {node.short_label || 'Exam'}
            </text>
            {/* Stable click target over rotating octagon */}
            <circle r={s * 1.2} fill="transparent" style={{ cursor: 'pointer' }} />
          </g>
        );
      })()}

      {/* CRAM exam hub — single octagon representing all exam CRAM content */}
      {node.type === 'cram_exam_hub' && (() => {
        const s = r * 1.1;
        const pts = [];
        for (let i = 0; i < 8; i++) {
          const angle = (Math.PI / 4) * i - Math.PI / 8;
          pts.push(`${s * Math.cos(angle)},${s * Math.sin(angle)}`);
        }
        const masteryPct = node.mastery_pct ?? 0;
        const counts = node.counts || {};
        const ready = node.decks_ready ?? 0;
        const total = node.decks_total ?? 0;
        const allReady = ready >= total && total > 0;
        return (
          <g>
            {/* Outer glow */}
            <polygon points={pts.map(p => {
              const [x, y] = p.split(',').map(Number);
              return `${x * 1.15},${y * 1.15}`;
            }).join(' ')}
              fill="none" stroke={OFF_RED} strokeWidth={0.8} opacity={0.25} />
            {/* Main octagon */}
            <polygon points={pts.join(' ')}
              fill="rgba(224, 106, 67, 0.08)" stroke={OFF_RED} strokeWidth={2} />
            {/* Mastery arc */}
            {masteryPct > 0 && (() => {
              const arcR = s + 6;
              const pct = Math.min(masteryPct / 100, 1);
              if (pct >= 1) {
                return <circle r={arcR} fill="none" stroke="#22c55e" strokeWidth={2.5} opacity={0.8} />;
              }
              const endAngle = -Math.PI / 2 + 2 * Math.PI * pct;
              const x2 = arcR * Math.cos(endAngle);
              const y2 = arcR * Math.sin(endAngle);
              const large = pct > 0.5 ? 1 : 0;
              return (
                <path d={`M 0 ${-arcR} A ${arcR} ${arcR} 0 ${large} 1 ${x2} ${y2}`}
                  fill="none" stroke="#22c55e" strokeWidth={2.5} strokeLinecap="round" opacity={0.7} />
              );
            })()}
            {/* Label */}
            <text textAnchor="middle" y={-8}
              fill="#fca5a5" fontSize={10} fontWeight={700} letterSpacing="1"
              fontFamily="var(--font-primary, sans-serif)">
              CRAM
            </text>
            {/* Category counts */}
            <text textAnchor="middle" y={5}
              fill="#d1d5db" fontSize={7} fontWeight={500}
              fontFamily="var(--font-primary, sans-serif)">
              {[counts.disease && `${counts.disease}D`, counts.treatment && `${counts.treatment}T`, counts.drug && `${counts.drug}X`].filter(Boolean).join(' / ')}
            </text>
            {/* Deck readiness or mastery */}
            <text textAnchor="middle" y={16}
              fill={allReady ? (masteryPct > 0 ? '#22c55e' : '#9ca3af') : '#6b7280'}
              fontSize={6} fontWeight={400}
              fontFamily="var(--font-primary, sans-serif)">
              {masteryPct > 0 ? `${masteryPct}% mastered` : allReady ? `${total} decks` : `${ready}/${total} decks`}
            </text>
            {/* Stable click target over octagon */}
            <circle r={s * 1.2} fill="transparent" style={{ cursor: 'pointer' }} />
          </g>
        );
      })()}

      {/* CRAM deck header — bar with deck title, or octagon for pending decks */}
      {node.type === 'cram_deck_header' && (() => {
        const isPending = node.pending;
        const masteryPct = node.mastery_pct ?? 0;

        if (isPending) {
          // Pending deck: single octagon
          const s = 18;
          const pts = [];
          for (let i = 0; i < 8; i++) {
            const angle = (Math.PI / 4) * i - Math.PI / 8;
            pts.push(`${s * Math.cos(angle)},${s * Math.sin(angle)}`);
          }
          return (
            <g>
              <polygon points={pts.join(' ')}
                fill="rgba(75, 85, 99, 0.08)" stroke="#4b5563" strokeWidth={1}
                strokeDasharray="3 2" opacity={0.5} />
              <text textAnchor="middle" y={-2}
                fill="#6b7280" fontSize={6} fontWeight={600} letterSpacing="0.3"
                fontFamily="var(--font-primary, sans-serif)">
                {(node.short_label || node.label || '').substring(0, 12).toUpperCase()}
              </text>
              <text textAnchor="middle" y={8}
                fill="#4b5563" fontSize={5} fontWeight={400} fontStyle="italic"
                fontFamily="var(--font-primary, sans-serif)">
                generating...
              </text>
            </g>
          );
        }

        // Active deck: bar with title
        const w = 100, h = 36;
        const gray = '#6b7280';
        return (
          <g>
            <rect x={-w/2} y={-h/2} width={w} height={h} rx={6}
              fill="rgba(107, 114, 128, 0.10)" stroke={gray} strokeWidth={1} opacity={0.6} />
            <text textAnchor="middle" y={masteryPct > 0 ? -4 : 2}
              fill="#d1d5db" fontSize={8} fontWeight={600} letterSpacing="0.3"
              fontFamily="var(--font-primary, sans-serif)">
              {(node.short_label || node.label || '').toUpperCase()}
            </text>
            {masteryPct > 0 && (
              <text textAnchor="middle" y={10}
                fill={masteryPct >= 100 ? '#22c55e' : '#9ca3af'} fontSize={7} fontWeight={500}
                fontFamily="var(--font-primary, sans-serif)">
                {masteryPct}% mastered
              </text>
            )}
          </g>
        );
      })()}

      {/* Solar system disease-treatment — cram_hub-matching hexagon with rotating rings */}
      {node.type === 'cram_disease_treatment' && (() => {
        const hexPts = [];
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i - Math.PI / 6;
          hexPts.push(`${Math.cos(angle) * r},${Math.sin(angle) * r}`);
        }
        const outerPts = [];
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i - Math.PI / 6;
          outerPts.push(`${Math.cos(angle) * (r + 4)},${Math.sin(angle) * (r + 4)}`);
        }
        return (
          <>
            <polygon points={outerPts.join(' ')} fill="none" stroke={color} strokeWidth={0.6} opacity={0.2}>
              <animateTransform attributeName="transform" type="rotate" values="0;-360" dur="20s" repeatCount="indefinite" />
            </polygon>
            <polygon points={hexPts.join(' ')} fill={dimColor} stroke={color} strokeWidth={2}>
              <animateTransform attributeName="transform" type="rotate" values="0;360" dur="30s" repeatCount="indefinite" />
            </polygon>
            <circle r={r * 0.55} fill={color} opacity={0.08}>
              <animate attributeName="opacity" values="0.06;0.18;0.06" dur="3s" repeatCount="indefinite" />
              <animate attributeName="r" values={`${r * 0.5};${r * 0.65};${r * 0.5}`} dur="3s" repeatCount="indefinite" />
            </circle>
          </>
        );
      })()}

      {/* Solar center mastery arc — removed (progress shown only on portal nodes) */}

      {/* Transparent hit target keeps animated CRAM nodes reliably clickable. */}
      {hasStableCramHitTarget && (
        <circle r={Math.max(r * 1.35, 18)} fill="transparent" />
      )}

      {/* 🔴 A GLYPH BODY HAS HOLES; THE DISC IT REPLACED DID NOT. A `bodyGlyph` node
          is clickable only where the mark has INK, so any gap inside the silhouette
          is dead. Measured 2026-08-26: the lungs (Respiratory, Derm & Eye) open at
          the airway gap between the two lobes, and `elementFromPoint` at that node's
          exact centre returned the background rect — the single most natural place to
          click did nothing. Every other organ happened to be solid at its centre,
          which is luck, not design, and a future mark can lose the same way.

          So restore exactly what the disc gave: a transparent circle at the node's OWN
          radius, drawn under the mark. `fill="transparent"` still takes pointer events
          (it is a painted fill with zero alpha), so a click over a hole falls through
          the glyph and lands here. Not larger than r — this is about giving the mark
          back the target its circle had, not letting it poach a neighbour's clicks. */}
      {node.bodyGlyph && isClickable && (
        <circle r={r} fill="transparent" />
      )}

      {/* Main circle (skip group_hub/cram shapes which use custom shapes; content nodes use the diamond above) */}
      {node.type !== 'group_hub' && node.type !== 'cram_hub' && node.type !== 'cram_disease' && node.type !== 'cram_treatment' && node.type !== 'cram_treatment_group' && node.type !== 'cram_portal' && node.type !== 'cram_deck_portal' && node.type !== 'cram_exam_center' && node.type !== 'cram_disease_treatment' && node.type !== 'cram_deck_header' && node.type !== 'cram_exam_hub' && node.type !== 'cram_drill_bank' && node.type !== 'dlc_areas_portal' && node.type !== 'content' && (
        <g>
          {/* CRAM drug — gentle float */}
          {node.type === 'cram_drug' && (
            <animateTransform attributeName="transform" type="translate" values="0,0;0,-1.5;0,0" dur="4s" repeatCount="indefinite" />
          )}
          {/* Body shape. A node carrying `bodyGlyph` draws that silhouette INSTEAD of
              the circle, with identical fill / stroke / weight / filter, so the mark
              IS the body rather than an overlay on it. Purely opt-in: absent the
              field every map keeps the circle it has always had. Consumed today by
              the Drug Lab's organ marks (data/areaGlyphs.js).

              The glyph's own content bbox is fitted to the node footprint (2r), NOT
              its authored 24x24 box — the box carries margins that shrink the shape,
              the same reason the iconFillPaths branch below takes an iconFillBBox.
              strokeWidth is divided back out by the scale so the outline renders at
              the weight a circle would have had. */}
          {(() => {
            const bodyStroke = node.type === 'final_boss' || node.type === 'htn_boss' ? 2.5 : node.type === 'boss' ? 2 : isPortal ? 1.8 : 1.5;
            const bodyFill = isMixed ? `url(#${gradId})` : dimColor;
            const bodyOpacity = isMixed ? 0.25 : node.node_kind === 'mini' ? 0.15 : 1;
            const bodyFilter = (isPortal && node.type !== 'htn_portal') ? 'url(#portal-glow)' : undefined;
            const bg = node.bodyGlyph;
            if (bg && Array.isArray(bg.paths) && bg.paths.length && bg.bbox) {
              const span = Math.max(bg.bbox.w, bg.bbox.h) || 24;
              const s = (r * 2) / span;
              const cx = bg.bbox.x + bg.bbox.w / 2;
              const cy = bg.bbox.y + bg.bbox.h / 2;
              return (
                <g transform={`scale(${s}) translate(${-cx}, ${-cy})`} filter={bodyFilter}>
                  {bg.paths.map((d, i) => (
                    <path
                      key={i}
                      d={d}
                      fill={bodyFill}
                      fillOpacity={bodyOpacity}
                      stroke={isMixed ? `url(#${gradId})` : color}
                      strokeWidth={bodyStroke / s}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  ))}
                </g>
              );
            }
            return (
              <circle
                r={r}
                fill={bodyFill}
                fillOpacity={bodyOpacity}
                stroke={isMixed ? `url(#${gradId})` : color}
                strokeWidth={bodyStroke}
                strokeDasharray={node.node_kind === 'mini' ? '4 3' : undefined}
                filter={bodyFilter}
              />
            );
          })()}
        </g>
      )}

      {/* Solar drug mastery arc — removed (progress shown only on portal nodes) */}

      {showClassCompletionCounter && (
        <text
          textAnchor="middle"
          y={3}
          fill="#fff"
          fontSize={Math.max(6, Math.min(8, r * 0.45))}
          fontWeight={700}
          fontFamily="var(--font-primary, sans-serif)"
          opacity={0.9}
          style={{ pointerEvents: 'none' }}
        >
          {classCompletedCount}/{classMemberCount}
        </text>
      )}

      {isRedNode && (
        <g data-testid={miniBossPending ? 'mini-boss-pending-indicator' : 'upload-match-red-node'} style={{ pointerEvents: 'none' }}>
          {/* Animated gold pulse ring — the shared "reserved-red" signal for BOTH
              mini-boss-pending and upload-matched nodes. Scales with `r`, so it reads
              cleanly on small class nodes AND large therapeutic-area hubs. */}
          <circle
            r={r + 5}
            fill="none"
            stroke={MINI_BOSS_PENDING_RING}
            strokeWidth={1.6}
            opacity={0.72}
          >
            <animate attributeName="r" values={`${r + 4};${r + 10};${r + 4}`} dur="1.8s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.72;0.18;0.72" dur="1.8s" repeatCount="indefinite" />
          </circle>
          {/* Combat glyph (gold center disc + dark sword) — renders for BOTH
              mini-boss-pending AND upload-match nodes. The 2026-06-24 "Fix B" had
              gated this to mini-boss-pending only (dropped the disc on matched nodes),
              but the preprod golden reference renders it on every red node — the gold
              disc IS the "yellow thing" on the matched hubs/center. Restored to match
              preprod (Edward 2026-06-25 parity-hotfix; supersedes Fix B). */}
          <>
            <circle r={Math.max(3.5, r * 0.22)} fill={MINI_BOSS_PENDING_RING} opacity={0.95} />
            <path
              d={`M0,${-r * 0.12} L0,${r * 0.06}`}
              stroke="#451a03"
              strokeWidth={1.4}
              strokeLinecap="round"
            />
            <circle cx={0} cy={r * 0.17} r={Math.max(0.7, r * 0.035)} fill="#451a03" />
          </>
        </g>
      )}

      {/* Keystone icon — embedded inside the circle for type='keystone'.
          Accepts node.iconPath (single SVG path string for the icon, drawn
          in a 24x24 viewBox like keystoneCards.jsx). Replaces boss/final_boss
          chrome (skull/crown) since keystones use their own identity glyph. */}
      {node.type === 'keystone' && node.iconPath && (
        <g transform={`translate(${-r * 0.65}, ${-r * 0.65}) scale(${(r * 1.3) / 24})`} style={{ pointerEvents: 'none' }}>
          <path
            d={node.iconPath}
            fill="none"
            stroke={color}
            strokeWidth={1.8}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      )}

      {/* Drug-card effect glyph — for non-keystone card_unlock nodes (type='topic')
          that carry node.iconPaths (an array of SVG path `d` strings, one per
          stroke). Same 24x24 transform + stroke idiom as the keystone block;
          color is the node's keystone-affinity color. Hidden once the card is
          owned so the completion checkmark below reads cleanly. */}
      {node.type === 'topic' && Array.isArray(node.iconPaths) && node.iconPaths.length > 0 && status !== 'completed' && (
        <g transform={`translate(${-r * 0.65}, ${-r * 0.65}) scale(${(r * 1.3) / 24})`} style={{ pointerEvents: 'none' }}>
          {node.iconPaths.map((d, i) => (
            <path
              key={i}
              d={d}
              fill="none"
              stroke={color}
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}
        </g>
      )}

      {/* Filled silhouette glyph — for special off-tree nodes (e.g. Psilocybin)
          that carry node.iconFillPaths drawn as a solid fill in the node color,
          scaled from their own iconFillViewBox. Like the stroke glyph above, it
          hides once owned so the completion checkmark reads cleanly. */}
      {node.type === 'topic' && Array.isArray(node.iconFillPaths) && node.iconFillPaths.length > 0 && status !== 'completed' && (() => {
        // Fit the glyph's content bbox tight to the node circle (the raw
        // viewBox carries margins that shrink the shape). Falls back to the
        // full viewBox if no bbox is provided.
        const bb = node.iconFillBBox;
        const span = bb ? Math.max(bb.w, bb.h) : (node.iconFillViewBox || 24);
        const s = (r * 1.4) / span;
        const cx = bb ? bb.minX + bb.w / 2 : (node.iconFillViewBox || 24) / 2;
        const cy = bb ? bb.minY + bb.h / 2 : (node.iconFillViewBox || 24) / 2;
        return (
          <g transform={`scale(${s}) translate(${-cx}, ${-cy})`} style={{ pointerEvents: 'none' }}>
            {node.iconFillPaths.map((d, i) => (
              <path key={i} d={d} fill={color} stroke="none" />
            ))}
          </g>
        );
      })()}

      {/* Inner glow for completed, cleared content, or covered cram (skip fountain/drill — handled below) */}
      {(status === 'completed' || (status === 'cleared' && node.type === 'content') || isCramCovered) && !isRedNode && node.type !== 'fountain' && node.type !== 'drill' && (
        <circle r={r * 0.5} fill={color} opacity={0.2} />
      )}

      {/* Checkmark for completed, cleared, or covered cram (skip fountain/drill — handled below) */}
      {(status === 'completed' || status === 'cleared' || isCramCovered) && !isRedNode && node.type !== 'fountain' && node.type !== 'drill' && (
        <path
          d={`M${-r*0.3},0 L${-r*0.1},${r*0.25} L${r*0.3},${-r*0.2}`}
          fill="none"
          stroke="#fff"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* Upload-match indicator retired 2026-06-24 (Edward): matched nodes now render
          as canonical RED NODES (reserved pure-red body + animated gold pulse ring),
          driven by `isRedNode` above + the shared red-node indicator block — identical
          to a mini-boss-pending node. The old node-colored crosshair reticle is gone. */}

      {/* Lock glyph — SKILL TREE ONLY. Gated on `node._stm` because that
          field is only stamped by skillTreeToMapData; upload-deck map nodes
          never carry it and therefore never render this glyph. Matches the
          white-stroke vocabulary of the completion check so it reads as a
          sibling icon. Sized as a fraction of node radius so it scales
          naturally. */}
      {node._stm && (status === 'locked' || status === 'dead_locked') && (node.type === 'topic' || node.type === 'keystone') && (
        <g opacity={0.85} style={{ pointerEvents: 'none' }}>
          {/* Shackle arc */}
          <path
            d={`M${-r*0.22},${-r*0.05} A${r*0.22},${r*0.22} 0 0 1 ${r*0.22},${-r*0.05}`}
            fill="none"
            stroke="#fff"
            strokeWidth={1.4}
            strokeLinecap="round"
          />
          {/* Body — rounded rect */}
          <rect
            x={-r*0.28}
            y={-r*0.05}
            width={r*0.56}
            height={r*0.42}
            rx={r*0.07}
            fill="#fff"
            opacity={0.92}
          />
          {/* Keyhole notch */}
          <line
            x1={0}
            y1={r*0.08}
            x2={0}
            y2={r*0.24}
            stroke="#0a0a0a"
            strokeWidth={1.2}
            strokeLinecap="round"
          />
        </g>
      )}


      {/* Bespoke boss hieroglyph — a combat-dynamic mark per enemy (bossGlyphs.jsx),
          in the skill-tree keystone idiom (24x24, centered, stroke=color). Sized
          at ~0.95r (27% smaller than the keystone 1.3r embed). Renders for any boss
          node that resolves a roster enemy; the per-type crown/skull/swords below
          render only when this is absent. */}
      {bossGlyph && status !== 'completed' && (
        <BossGlyphGroup
          glyph={bossGlyph}
          transform={bossGlyphTransform(r)}
          stroke={isMixed ? `url(#${gradId})` : color}
          style={{ color, pointerEvents: 'none' }}
        />
      )}

      {/* Crown icon for final boss */}
      {node.type === 'final_boss' && status !== 'completed' && !bossGlyph && (
        <g transform="translate(-8,-6) scale(0.7)">
          <path d="M1 18h22v3H1z M1 18l4-12 4 5 3-8 3 8 4-5 4 12" fill="none" stroke={isMixed ? `url(#${gradId})` : color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        </g>
      )}

      {/* Skull-ish indicator for boss */}
      {node.type === 'boss' && status !== 'completed' && !bossGlyph && (
        <>
          <circle cx={-3} cy={-2} r={2} fill={color} opacity={0.6} />
          <circle cx={3} cy={-2} r={2} fill={color} opacity={0.6} />
          <path d="M-2,3 L0,2 L2,3" fill="none" stroke={color} strokeWidth={1} opacity={0.5} />
        </>
      )}

      {/* Group hub — rounded rect with completion counter inside */}
      {node.type === 'group_hub' && (() => {
        const w = 90, h = 32;
        const completed = node.completed_count ?? 0;
        const total = node.slug_count ?? 0;
        return (
          <>
            <rect x={-w/2} y={-h/2} width={w} height={h} rx={8}
              fill={dimColor} stroke={color} strokeWidth={1.5} opacity={0.9} />
            <text textAnchor="middle" y={-3}
              fill={color} fontSize={8} fontWeight={700}
              fontFamily="var(--font-primary, sans-serif)">
              {splitLabel(node.label, 16)[0]}
            </text>
            {splitLabel(node.label, 16)[1] && (
              <text textAnchor="middle" y={7}
                fill={color} fontSize={7} fontWeight={600} opacity={0.8}
                fontFamily="var(--font-primary, sans-serif)">
                {splitLabel(node.label, 16)[1]}
              </text>
            )}
            <text textAnchor="middle" y={splitLabel(node.label, 16)[1] ? 17 : 11}
              fill={color} fontSize={7} fontWeight={600} opacity={0.7}
              fontFamily="var(--font-primary, sans-serif)">
              {completed}/{total}
            </text>
          </>
        );
      })()}

      {/* Group boss skull indicator (same as boss) */}
      {node.type === 'group_boss' && status !== 'completed' && !bossGlyph && (
        <>
          <circle cx={-3} cy={-2} r={2} fill={color} opacity={0.6} />
          <circle cx={3} cy={-2} r={2} fill={color} opacity={0.6} />
          <path d="M-2,3 L0,2 L2,3" fill="none" stroke={color} strokeWidth={1} opacity={0.5} />
        </>
      )}

      {/* Area boss crown indicator (same as final_boss) */}
      {node.type === 'area_boss' && status !== 'completed' && !bossGlyph && (
        <g transform="translate(-8,-6) scale(0.7)">
          <path d="M1 18h22v3H1z M1 18l4-12 4 5 3-8 3 8 4-5 4 12" fill="none" stroke={isMixed ? `url(#${gradId})` : color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        </g>
      )}

      {/* HTN Regimen boss — crown (the culminating combined fight). */}
      {node.type === 'htn_boss' && status !== 'completed' && !bossGlyph && (
        <g transform="translate(-8,-6) scale(0.7)">
          <path d="M1 18h22v3H1z M1 18l4-12 4 5 3-8 3 8 4-5 4 12" fill="none" stroke={isMixed ? `url(#${gradId})` : color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        </g>
      )}

      {/* Synthesis node — small diamond overlay */}
      {node.type === 'synthesis' && status !== 'completed' && (
        <rect
          x={-r * 0.6}
          y={-r * 0.6}
          width={r * 1.2}
          height={r * 1.2}
          transform="rotate(45)"
          fill="none"
          stroke={color}
          strokeWidth={1}
          opacity={0.5}
        />
      )}

      {/* Tier boss — hexagon outline + crossed swords icon */}
      {node.type === 'tier_boss' && (
        <>
          <polygon
            points={hexPoints(r * 0.85)}
            fill="none"
            stroke={color}
            strokeWidth={1.2}
            opacity={0.5}
          />
          {status !== 'completed' && !bossGlyph && (
            <g opacity={0.7}>
              <line x1={-6} y1={-6} x2={6} y2={6} stroke={color} strokeWidth={1.5} strokeLinecap="round" />
              <line x1={6} y1={-6} x2={-6} y2={6} stroke={color} strokeWidth={1.5} strokeLinecap="round" />
              <circle cx={0} cy={0} r={2.5} fill={color} opacity={0.3} />
            </g>
          )}
        </>
      )}

      {/* Apothecarius — pulsing aura + crown icon */}
      {node.type === 'apothecarius' && (
        <>
          <circle r={r + 8} fill="none" stroke={color} strokeWidth={0.8} opacity={0.2}>
            <animate attributeName="r" values={`${r + 5};${r + 12};${r + 5}`} dur="4s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.25;0.08;0.25" dur="4s" repeatCount="indefinite" />
          </circle>
          {status !== 'completed' && !bossGlyph && (
            <g transform="translate(-9,-7) scale(0.75)">
              <path d="M1 18h22v3H1z M1 18l4-12 4 5 3-8 3 8 4-5 4 12" fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
            </g>
          )}
        </>
      )}

      {/* Clinical round — rounded-rect clipboard shape + medical cross */}
      {(node.type === 'clinical_round' || node.type === 'inspector_walkthrough') && (
        <>
          <rect
            x={-r * 0.55} y={-r * 0.65}
            width={r * 1.1} height={r * 1.3}
            rx={3} ry={3}
            fill="none" stroke={color} strokeWidth={1.2} opacity={0.6}
          />
          {/* Clipboard clip tab at top */}
          <rect
            x={-r * 0.25} y={-r * 0.78}
            width={r * 0.5} height={r * 0.25}
            rx={2} ry={2}
            fill={color} opacity={0.4}
          />
          {/* Medical cross / walkthrough indicator */}
          {status !== 'completed' && (
            node.type === 'clinical_round' ? (
              <g opacity={0.7}>
                <line x1={0} y1={-4} x2={0} y2={5} stroke={color} strokeWidth={2} strokeLinecap="round" />
                <line x1={-4.5} y1={0.5} x2={4.5} y2={0.5} stroke={color} strokeWidth={2} strokeLinecap="round" />
              </g>
            ) : (
              <g opacity={0.75}>
                <circle cx={-2.5} cy={-1.5} r={1.3} fill={color} />
                <circle cx={2.8} cy={3.2} r={1.3} fill={color} />
                <path d="M-1.5,-0.5 L0.5,1 L4,-2" fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
              </g>
            )
          )}
        </>
      )}

      {/* Rx Detail / Statute Detail — pill or statute card icon */}
      {(node.type === 'rx_detail' || node.type === 'statute_detail') && status !== 'completed' && (
        <g>
          {node.type === 'rx_detail' ? (
            <>
              <rect x={-3} y={-5} width={6} height={10} rx={3} fill="white" opacity={0.9} />
              <line x1={-3} y1={0} x2={3} y2={0} stroke={color} strokeWidth={1} opacity={0.7} />
            </>
          ) : (
            <>
              <rect x={-4} y={-5.5} width={8} height={11} rx={1.5} fill="none" stroke="white" strokeWidth={1.3} opacity={0.9} />
              <line x1={-2.5} y1={-2} x2={2.5} y2={-2} stroke="white" strokeWidth={1} opacity={0.7} />
              <line x1={-2.5} y1={0.5} x2={2.5} y2={0.5} stroke="white" strokeWidth={1} opacity={0.7} />
              <line x1={-2.5} y1={3} x2={1.2} y2={3} stroke="white" strokeWidth={1} opacity={0.7} />
            </>
          )}
        </g>
      )}

      {/* Safety / Compliance Check — shield outline with plus or check */}
      {(node.type === 'safety' || node.type === 'compliance_check') && status !== 'completed' && (
        <g>
          <path
            d="M0,-8 L7,-5 L7,1 C7,5 4,8 0,10 C-4,8 -7,5 -7,1 L-7,-5 Z"
            fill="none" stroke="white" strokeWidth={1.5} opacity={0.85}
          />
          {node.type === 'safety' ? (
            <>
              <line x1={0} y1={-3} x2={0} y2={5} stroke="white" strokeWidth={1.8} strokeLinecap="round" opacity={0.8} />
              <line x1={-4} y1={1} x2={4} y2={1} stroke="white" strokeWidth={1.8} strokeLinecap="round" opacity={0.8} />
            </>
          ) : (
            <path d="M-3,1 L-0.5,3.8 L4,-2.2" fill="none" stroke="white" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" opacity={0.85} />
          )}
        </g>
      )}

      {/* Reserved MPJE types — minimal skeleton markers so future content can render safely */}
      {MPJE_RESERVED_NODE_TYPES.has(node.type) && status !== 'completed' && (
        <g opacity={0.75}>
          <circle r={r * 0.38} fill="none" stroke={color} strokeWidth={1.2} />
          <line x1={-r * 0.18} y1={0} x2={r * 0.18} y2={0} stroke={color} strokeWidth={1.2} strokeLinecap="round" />
        </g>
      )}

      {/* Hexagon overlay for shared nodes */}
      {node.type === 'shared' && (
        <polygon
          points={hexPoints(r * 0.75)}
          fill="none"
          stroke={color}
          strokeWidth={0.8}
          opacity={0.5}
        />
      )}

      {/* Portal gateway — ethereal swirl icon (appears on non-death deck maps) */}
      {node.type === 'portal_gateway' && (
        <>
          {/* Outer pulse ring */}
          <circle r={r + 4} fill="none" stroke={PORTAL_WHITE} strokeWidth={0.8} opacity={0.3}>
            <animate attributeName="r" values={`${r + 2};${r + 6};${r + 2}`} dur="3s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.3;0.1;0.3" dur="3s" repeatCount="indefinite" />
          </circle>
          {/* Inner swirl — a simple spiral hint */}
          <circle cx={0} cy={0} r={r * 0.35} fill="none" stroke={PORTAL_WHITE} strokeWidth={1.2} opacity={0.6} />
          <path
            d={`M0,${-r*0.5} A${r*0.5},${r*0.5} 0 1,1 ${r*0.5},0`}
            fill="none" stroke={PORTAL_WHITE} strokeWidth={1} opacity={0.5}
          />
          <path
            d={`M0,${r*0.5} A${r*0.5},${r*0.5} 0 1,1 ${-r*0.5},0`}
            fill="none" stroke={PORTAL_WHITE} strokeWidth={1} opacity={0.5}
          />
        </>
      )}

      {/* DLC areas portal — return-to-overview node inside NAPLEX/PTCB maps.
          Vortex-style like CRAM portal, with the folded-map icon at center. */}
      {node.type === 'dlc_areas_portal' && (() => {
        const pc = color;
        return (
          <>
            <g pointerEvents="none">
              {/* Outer pulse ring */}
              <circle r={r + 4} fill="none" stroke={pc} strokeWidth={0.9} opacity={0.3}>
                <animate attributeName="r" values={`${r + 2};${r + 7};${r + 2}`} dur="3.2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.3;0.08;0.3" dur="3.2s" repeatCount="indefinite" />
              </circle>
              {/* Secondary pulse — offset */}
              <circle r={r + 6} fill="none" stroke={pc} strokeWidth={0.6} opacity={0.18}>
                <animate attributeName="r" values={`${r + 4};${r + 9};${r + 4}`} dur="4.2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.18;0.04;0.18" dur="4.2s" repeatCount="indefinite" />
              </circle>
              {/* Base disc */}
              <circle r={r} fill={dimColor} stroke={pc} strokeWidth={1.6} />
              {/* Folded-map icon — same glyph as the ProgressMap empty-state.
                  Kept smaller than the node disc so the portal ring reads. */}
              {(() => {
                const k = r / 24; // icon ≈ 50% of node diameter
                const tx = -12 * k, ty = -12 * k;
                return (
                  <g transform={`translate(${tx}, ${ty}) scale(${k})`} opacity={0.95}>
                    <polygon
                      points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"
                      fill="none" stroke="#fff" strokeWidth={1.8}
                      strokeLinecap="round" strokeLinejoin="round"
                    />
                    <line x1="8" y1="2" x2="8" y2="18" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" />
                    <line x1="16" y1="6" x2="16" y2="22" stroke="#fff" strokeWidth={1.8} strokeLinecap="round" />
                  </g>
                );
              })()}
            </g>
            {/* Stable click target */}
            <circle r={Math.max(r * 1.35, 20)} fill="transparent" pointerEvents="all" style={{ cursor: 'pointer' }} />
          </>
        );
      })()}

      {/* Resuscitate node — larger, brighter, with converging arrows (appears on death deck map) */}
      {node.type === 'resuscitate' && (
        <>
          {/* Double pulse rings */}
          <circle r={r + 3} fill="none" stroke={PORTAL_ANCHOR} strokeWidth={1} opacity={0.35}>
            <animate attributeName="r" values={`${r + 2};${r + 7};${r + 2}`} dur="2.5s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.35;0.08;0.35" dur="2.5s" repeatCount="indefinite" />
          </circle>
          <circle r={r + 5} fill="none" stroke={PORTAL_ANCHOR} strokeWidth={0.6} opacity={0.2}>
            <animate attributeName="r" values={`${r + 4};${r + 9};${r + 4}`} dur="3.5s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.2;0.05;0.2" dur="3.5s" repeatCount="indefinite" />
          </circle>
          {/* Inner diamond/lozenge shape — distinguishes from gateway */}
          <rect
            x={-r * 0.4} y={-r * 0.4}
            width={r * 0.8} height={r * 0.8}
            transform="rotate(45)"
            fill="none" stroke={PORTAL_ANCHOR} strokeWidth={1.2} opacity={0.7}
            rx={1}
          />
          {/* Small center dot */}
          <circle r={2} fill={PORTAL_ANCHOR} opacity={0.8} />
        </>
      )}

      {/* HTN "Regimen" portal — the luminous gateway read comes from this
          GLOW GROUP (the soft `url(#portal-glow)` lives HERE, on the thin rings +
          ECG glyph), NOT from the main disc. The main disc renders flat-translucent
          like every other node body (htn_portal is excluded from the disc-level
          portal-glow above), so it reads as warm-off-white GLASS with the void
          showing through — never an opaque beveled medallion. The ECG glyph is
          OFF-WHITE (in-family with the gateway/resuscitate swirl convention); the
          cardio/regimen brand cue is a single thin EMBER halo ring (canon §6),
          keeping #ef4444 reserved. (2026-06-30 cohesion critique round 3: the
          round-2 build had the glow on the filled disc, which bloomed it into a
          chrome-bezel coin — all four lenses flagged it P0.) */}
      {node.type === 'htn_portal' && (
        <g pointerEvents="none" filter="url(#portal-glow)">
          {/* Soft luminous warm CORE — a faint #efe6dd fill that the group's
              portal-glow blooms into a warm inner glow, lifting the disc BODY so it
              reads luminous like the gateway (0.2 fill) / anchor (0.25 fill) portal
              discs WITHOUT re-introducing the round-3 hard-bevel medallion (that came
              from the glow blooming the disc STROKE — a fill-only soft core has no
              rim/bevel). The flat translucent main disc still shows the void through
              its EDGES; this lifts only the centre. (round-4 cohesion: family-luminance.) */}
          <circle r={r * 0.92} fill={HTN_PORTAL} opacity={0.16} />
          {/* Thin ember halo ring — the cardio/regimen brand accent (lifted so it
              actually registers as the 'Regimen' ember cue, not a faint smudge). */}
          <circle r={r + 1.5} fill="none" stroke={HTN_PORTAL_ACCENT} strokeWidth={1.4} opacity={0.72} />
          {/* Outer pulse ring (soft — the glow group supplies the luminance). */}
          <circle r={r + 4} fill="none" stroke={HTN_PORTAL} strokeWidth={1} opacity={0.42}>
            <animate attributeName="r" values={`${r + 2};${r + 7};${r + 2}`} dur="3s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.42;0.16;0.42" dur="3s" repeatCount="indefinite" />
          </circle>
          {/* Secondary offset pulse */}
          <circle r={r + 7} fill="none" stroke={HTN_PORTAL} strokeWidth={0.7} opacity={0.24}>
            <animate attributeName="r" values={`${r + 5};${r + 10};${r + 5}`} dur="4.2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.24;0.07;0.24" dur="4.2s" repeatCount="indefinite" />
          </circle>
          {/* Tailored glyph — a PRESSURE GAUGE / manometer dial (a blood-pressure
              signal), replacing the prior single-beat ECG trace. The ECG read as
              heart-RHYTHM / EKG and could be mistaken for a cardiac-rhythm section
              rather than hypertension (Edward 2026-07-04: "more indicative of blood
              pressure"). A speedometer-style dial arc + needle sweeping up-right +
              hub + end/apex ticks, drawn in the portal off-white so it stays in the
              portal family; the ember halo above still supplies the cardio/BP brand
              cue. Stroke ~2.1 so the off-white silhouette holds luminance off-peak
              (per the prior ECG antialiasing note). */}
          <g fill="none" stroke={HTN_PORTAL} strokeWidth={2.1} strokeLinecap="round" strokeLinejoin="round">
            {/* Dial face — top semicircular gauge dome (opens at the base). */}
            <path d={`M${-r * 0.6},${r * 0.32} A${r * 0.6},${r * 0.6} 0 0 1 ${r * 0.6},${r * 0.32}`} />
            {/* Ticks at the two ends + apex so it reads as a measured DIAL, not a bump. */}
            <path d={`M${-r * 0.6},${r * 0.32} L${-r * 0.47},${r * 0.32}`} strokeWidth={1.3} opacity={0.85} />
            <path d={`M0,${-r * 0.28} L0,${-r * 0.15}`} strokeWidth={1.3} opacity={0.85} />
            <path d={`M${r * 0.6},${r * 0.32} L${r * 0.47},${r * 0.32}`} strokeWidth={1.3} opacity={0.85} />
            {/* Needle sweeping up-right — a live pressure reading. */}
            <path d={`M0,${r * 0.32} L${r * 0.36},${-r * 0.14}`} strokeWidth={2.2} />
            {/* Hub. */}
            <circle cx={0} cy={r * 0.32} r={r * 0.08} fill={HTN_PORTAL} stroke="none" />
          </g>
        </g>
      )}

      {/* CRAM portal — vortex with orbiting particles and inner swirl */}
      {node.type === 'cram_portal' && (() => {
        const pc = color; // uses computed color (red for enter, blue for back)
        const s = r * 0.65; // swirl arm radius
        const _progress = (node.progress_pct || 0) / 100;
        const _arcR = r + 4;
        return (
          <>
            <g pointerEvents="none">
              {/* Progress ring (only when progress_pct > 0) */}
              {_progress > 0 && (() => {
                const startAngle = -Math.PI / 2; // 12 o'clock
                const endAngle = startAngle + _progress * 2 * Math.PI;
                const largeArc = _progress > 0.5 ? 1 : 0;
                const sx = _arcR * Math.cos(startAngle);
                const sy = _arcR * Math.sin(startAngle);
                const ex = _arcR * Math.cos(endAngle);
                const ey = _arcR * Math.sin(endAngle);
                return (
                  <>
                    {/* Background track */}
                    <circle r={_arcR} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth={3} />
                    {/* Progress arc */}
                    {_progress >= 1 ? (
                      <circle r={_arcR} fill="none" stroke="#22c55e" strokeWidth={3} />
                    ) : (
                      <path
                        d={`M${sx},${sy} A${_arcR},${_arcR} 0 ${largeArc},1 ${ex},${ey}`}
                        fill="none" stroke="#22c55e" strokeWidth={3} strokeLinecap="round"
                      />
                    )}
                  </>
                );
              })()}
              {/* Base circle */}
              <circle r={r} fill={dimColor} stroke={pc} strokeWidth={1.8} />
              {/* Outer pulse ring */}
              <circle r={r + 3} fill="none" stroke={pc} strokeWidth={0.8} opacity={0.25}>
                <animate attributeName="r" values={`${r + 2};${r + 6};${r + 2}`} dur="3s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.25;0.06;0.25" dur="3s" repeatCount="indefinite" />
              </circle>
              {/* Second pulse ring — offset phase */}
              <circle r={r + 5} fill="none" stroke={pc} strokeWidth={0.5} opacity={0.12}>
                <animate attributeName="r" values={`${r + 4};${r + 8};${r + 4}`} dur="4s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.12;0.03;0.12" dur="4s" repeatCount="indefinite" />
              </circle>
              {/* 3 orbiting particles at different speeds */}
              <circle r={1.8} fill={pc} opacity={0.6}>
                <animateMotion dur="3s" repeatCount="indefinite" path={`M${r + 2},0 A${r + 2},${r + 2} 0 1,1 ${-(r + 2)},0 A${r + 2},${r + 2} 0 1,1 ${r + 2},0`} />
              </circle>
              <circle r={1.2} fill="#fff" opacity={0.4}>
                <animateMotion dur="4.5s" repeatCount="indefinite" path={`M0,${r + 1} A${r + 1},${r + 1} 0 1,0 0,${-(r + 1)} A${r + 1},${r + 1} 0 1,0 0,${r + 1}`} />
              </circle>
              <circle r={1} fill={pc} opacity={0.45}>
                <animateMotion dur="2.2s" repeatCount="indefinite" path={`M${-(r - 2)},0 A${r - 2},${r - 2} 0 1,0 ${r - 2},0 A${r - 2},${r - 2} 0 1,0 ${-(r - 2)},0`} />
              </circle>
              {/* Inner swirl — two spiral arms rotating */}
              <g opacity={0.55}>
                <animateTransform attributeName="transform" type="rotate" values="0;360" dur="4s" repeatCount="indefinite" />
                {/* Spiral arm 1 */}
                <path
                  d={`M0,0 Q${s * 0.5},${-s * 0.3} ${s * 0.2},${-s} Q${-s * 0.2},${-s * 0.6} ${-s * 0.6},${-s * 0.1}`}
                  fill="none" stroke={pc} strokeWidth={1.2} strokeLinecap="round"
                />
                {/* Spiral arm 2 (180deg offset) */}
                <path
                  d={`M0,0 Q${-s * 0.5},${s * 0.3} ${-s * 0.2},${s} Q${s * 0.2},${s * 0.6} ${s * 0.6},${s * 0.1}`}
                  fill="none" stroke={pc} strokeWidth={1.2} strokeLinecap="round"
                />
              </g>
              {/* Center glow dot */}
              <circle r={2} fill={pc} opacity={0.7}>
                <animate attributeName="r" values="1.5;2.5;1.5" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.5;0.9;0.5" dur="2s" repeatCount="indefinite" />
              </circle>
            </g>
            {/* Stable click target over animated portal */}
            <circle r={Math.max(r * 1.35, 18)} fill="transparent" pointerEvents="all" style={{ cursor: 'pointer' }} />
          </>
        );
      })()}

      {/* CRAM hub — category letter (static, over rotating hex) */}
      {node.type === 'cram_hub' && (
        <text x={0} y={1} textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={11} fontWeight="700" opacity={0.9} style={{ pointerEvents: 'none' }}>
          {node.cram_category === 'disease' ? 'D' : node.cram_category === 'treatment' ? 'T' : 'Rx'}
        </text>
      )}

      {/* (drill fountain rendering merged into fountain/drill block below) */}

      {/* Dialysis/drill animated water rendering — dialysis nodes never show "completed" checkmark,
           they always show their current water state (full, partial, empty/recharging, or passive) */}
      {(node.type === 'fountain' || node.type === 'drill') && (() => {
        const fhp = node.fountain_hp ?? node.fountain_max ?? 50;
        const fmax = node.fountain_max || 50;
        const fill = Math.max(0, Math.min(1, fhp / fmax));
        const clipId = `fc-${node.id}`;
        const surfaceY = r - (fill * 2 * r);
        const isPassiveNode = status === 'passive';
        const isEarnedPassiveNode = isPassiveNode && node.passive_earned;
        const isEmpty = fill === 0 && !isPassiveNode;
        const isLowHp = fill > 0 && fill < 0.25;
        const hasWater = fill > 0 && !isPassiveNode;
        const liqColor = isLowHp ? '#ef4444' : fill < 0.5 ? '#60a5fa' : BLUE;
        const ext = r * 8;
        const wl = r * 1.6;

        return (
          <>
            <defs>
              <clipPath id={clipId}><circle r={r - 0.75} /></clipPath>
            </defs>

            {/* Low HP warning pulse */}
            {isLowHp && (
              <circle r={r + 2} fill="none" stroke="#ef4444" strokeWidth={0.8} opacity={0.3}>
                <animate attributeName="opacity" values="0.15;0.5;0.15" dur="1.8s" repeatCount="indefinite" />
                <animate attributeName="r" values={`${r + 1};${r + 4};${r + 1}`} dur="1.8s" repeatCount="indefinite" />
              </circle>
            )}

            {/* Active water with flowing waves */}
            {hasWater && (
              <g clipPath={`url(#${clipId})`}>
                <rect x={-r} y={surfaceY} width={r * 2} height={r * 3} fill={liqColor} opacity={0.2} />
                <path d={_wavePath(wl * 1.4, 2.5, surfaceY + 1, ext)} fill={liqColor} opacity={0.12}>
                  <animateTransform attributeName="transform" type="translate"
                    values={`0,0; ${-wl * 1.4},0`} dur="3.5s" repeatCount="indefinite" />
                </path>
                <path d={_wavePath(wl, 2, surfaceY - 0.5, ext)} fill={liqColor} opacity={0.15}>
                  <animateTransform attributeName="transform" type="translate"
                    values={`0,0; ${wl},0`} dur="2.5s" repeatCount="indefinite" />
                </path>
                {fill < 1 && (
                  <path d={_wavePath(wl * 0.7, 1.8, surfaceY - 1, ext)} fill={liqColor} opacity={0.18}>
                    <animateTransform attributeName="transform" type="translate"
                      values={`0,0; ${-wl * 0.7},0`} dur="1.8s" repeatCount="indefinite" />
                  </path>
                )}
                {fill > 0.15 && [0, 1].map(i => (
                  <circle key={`fb${i}`} cx={(i - 0.5) * r * 0.5} cy={r * 0.4} r={0.7} fill="white" opacity={0}>
                    <animate attributeName="cy" values={`${r * 0.4};${surfaceY}`} dur={`${2 + i * 0.8}s`} begin={`${i * 0.7}s`} repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0;0.2;0" dur={`${2 + i * 0.8}s`} begin={`${i * 0.7}s`} repeatCount="indefinite" />
                  </circle>
                ))}
              </g>
            )}

            {/* Empty/recharging — dashed droplet + clock */}
            {isEmpty && (
              <g transform={`scale(${r / 14})`} opacity={0.4}>
                <path d="M0,-4 C-2.5,-0.5 -3.5,2 -3.5,3.5 C-3.5,5.4 -1.9,7 0,7 C1.9,7 3.5,5.4 3.5,3.5 C3.5,2 2.5,-0.5 0,-4Z"
                  fill="none" stroke="#666" strokeWidth={0.8} strokeDasharray="1.5 1" />
                <circle cx={0} cy={2.5} r={2} fill="none" stroke="#666" strokeWidth={0.6} />
                <line x1={0} y1={1.2} x2={0} y2={2.5} stroke="#666" strokeWidth={0.6} />
                <line x1={0} y1={2.5} x2={1} y2={3.2} stroke="#666" strokeWidth={0.6} />
              </g>
            )}

            {/* Passive (100% trial) — golden droplet */}
            {isPassiveNode && !isEarnedPassiveNode && (
              <g opacity={0.85}>
                <circle r={r + 2} fill="none" stroke={GOLD} strokeWidth={0.6} opacity={0.2}>
                  <animate attributeName="opacity" values="0.1;0.3;0.1" dur="3s" repeatCount="indefinite" />
                  <animate attributeName="r" values={`${r + 1};${r + 4};${r + 1}`} dur="3s" repeatCount="indefinite" />
                </circle>
                <circle r={r - 1} fill={GOLD} fillOpacity={0.06} clipPath={`url(#${clipId})`}>
                  <animate attributeName="fill-opacity" values="0.04;0.1;0.04" dur="3s" repeatCount="indefinite" />
                </circle>
                <g transform={`scale(${r / 14})`}>
                  <path d="M0,-4 C-2.5,-0.5 -3.5,2 -3.5,3.5 C-3.5,5.4 -1.9,7 0,7 C1.9,7 3.5,5.4 3.5,3.5 C3.5,2 2.5,-0.5 0,-4Z"
                    fill={GOLD} fillOpacity={0.4} stroke="#fde68a" strokeWidth={0.8}>
                    <animate attributeName="fill-opacity" values="0.3;0.55;0.3" dur="2.5s" repeatCount="indefinite" />
                  </path>
                  <ellipse cx={-1} cy={0.5} rx={0.7} ry={1.5} fill="white" opacity={0.2} />
                </g>
              </g>
            )}

            {/* Earned passive (100 HP drained) — cyan droplet + infinity */}
            {isEarnedPassiveNode && (
              <g opacity={0.85}>
                <circle r={r + 2} fill="none" stroke={EARNED_CYAN} strokeWidth={0.6} opacity={0.2}>
                  <animate attributeName="opacity" values="0.1;0.35;0.1" dur="3s" repeatCount="indefinite" />
                  <animate attributeName="r" values={`${r + 1};${r + 4};${r + 1}`} dur="3s" repeatCount="indefinite" />
                </circle>
                <g clipPath={`url(#${clipId})`}>
                  <circle r={r} fill={EARNED_CYAN} fillOpacity={0.08} />
                  <path d={_wavePath(wl * 1.2, 1.8, -r * 0.3, ext)} fill={EARNED_CYAN} opacity={0.08}>
                    <animateTransform attributeName="transform" type="translate"
                      values={`0,0; ${-wl * 1.2},0`} dur="4s" repeatCount="indefinite" />
                  </path>
                </g>
                <g transform={`scale(${r / 14})`}>
                  <path d="M0,-4 C-2.5,-0.5 -3.5,2 -3.5,3.5 C-3.5,5.4 -1.9,7 0,7 C1.9,7 3.5,5.4 3.5,3.5 C3.5,2 2.5,-0.5 0,-4Z"
                    fill={EARNED_CYAN} fillOpacity={0.3} stroke={EARNED_CYAN} strokeWidth={0.8}>
                    <animate attributeName="fill-opacity" values="0.25;0.45;0.25" dur="3s" repeatCount="indefinite" />
                  </path>
                  <path d="M-2,3 C-2,1.8 -0.8,1.2 0,2.5 C0.8,1.2 2,1.8 2,3 C2,4.2 0.8,4.8 0,3.5 C-0.8,4.8 -2,4.2 -2,3Z"
                    fill="none" stroke="white" strokeWidth={0.6} opacity={0.5} />
                </g>
              </g>
            )}

            {/* Droplet icon for active water states */}
            {hasWater && (
              <g transform={`scale(${r / 14})`}>
                <path d="M0,-4 C-2.5,-0.5 -3.5,2 -3.5,3.5 C-3.5,5.4 -1.9,7 0,7 C1.9,7 3.5,5.4 3.5,3.5 C3.5,2 2.5,-0.5 0,-4Z"
                  fill={liqColor} fillOpacity={fill > 0.5 ? 0.25 : 0.12}
                  stroke={liqColor} strokeWidth={0.8} opacity={0.7} />
              </g>
            )}
          </>
        );
      })()}

      {/* Star icon for trial nodes */}
      {node.type === 'trial' && status !== 'completed' && (
        <g transform="translate(0,0)">
          <polygon
            points="0,-6 1.8,-2 6,-2 2.7,1.2 4,5.5 0,3 -4,5.5 -2.7,1.2 -6,-2 -1.8,-2"
            fill="none"
            stroke={color}
            strokeWidth={1.2}
            opacity={0.7}
          />
        </g>
      )}

      {/* Label — skip for group_hub/deck_portal/exam_center/deck_header/exam_hub.
          On the CRAM final boss map (solar system), labels are hover-only to reduce clutter.
          On all other maps (learn, per-deck CRAM), labels are always visible. */}
      {node.type !== 'group_hub' && node.type !== 'cram_deck_portal' && node.type !== 'cram_exam_center' && node.type !== 'cram_deck_header' && node.type !== 'cram_exam_hub' && (
        // Solar system nodes (planets + moons) = hover-only labels; everything else = always show
        (node._planetOrbit || node._moonOrbitR) ? isHovered : true
      ) && (() => {
        const fullLabel = node.label || node.short_label || '';
        // Any node with _radialAngle gets radial label positioning
        const isRadialLabel = node._radialAngle != null;
        // Split on explicit newlines first (used by boss labels with topic names)
        const segments = fullLabel.split('\n');
        // Scale font with node radius for variable-sized nodes
        const isSolarNode = node.type === 'cram_disease_treatment' || node.type === 'cram_solar_drug';
        // Drug Lab 'All Systems' compass: a system HEADING is the title of its
        // panel, so it is set apart typographically — larger, heavier, and in the
        // system's own colour — instead of sharing the 9px grey every class node
        // uses. Without this the heading is just another node with a longer name,
        // which is most of why the eight themes were hard to tell apart.
        const isHubHeading = !!(node.meta && node.meta.hub_heading);
        const isHubClass = !!(node.meta && typeof node.meta.hub_cluster === 'number');
        const fs = isHubHeading ? 8 : isHubClass ? 7
          : node.type === 'final_boss' ? 10 : node.type === 'apothecarius' ? 10 : node.type === 'tier_boss' ? 8 : 9;
        const baseY = r + 12;
        const allLines = [];
        // no_truncate (Drug Lab nodes): show the FULL title, word-wrapped over as
        // many lines as needed, never clamped with an ellipsis.
        const noTrunc = !!node.no_truncate;
        // Hub class names wrap narrow (12) rather than at the usual 22. Five classes
        // ring a ~61px radius, so adjacent labels get ~72px of chord between them;
        // at the default wrap a 21-char one-liner like "Vitamin K Antagonists" is
        // ~95px and the declutter pass has to shove it out of the disc to fit.
        const maxChars = isSolarNode ? 50
          : isHubClass ? 12
          : noTrunc ? (isRadialLabel ? 18 : 22) : isRadialLabel ? 14 : 20;
        segments.forEach(seg => {
          // In radial layouts, skip dim parenthetical lines to reduce clutter
          if (isRadialLabel && !noTrunc && seg.startsWith('(')) return;
          // Strip trailing parenthetical only from non-parenthesized segments
          // (kept intact for no_truncate nodes — the full title is the point).
          const cleaned = (noTrunc || seg.startsWith('(')) ? seg : seg.replace(/\s*\([^)]*\)\s*$/, '');
          const lines = noTrunc ? wrapFull(cleaned, maxChars) : splitLabel(cleaned, maxChars);
          lines.forEach(line => allLines.push({ text: line, dim: !noTrunc && seg.startsWith('(') }));
        });

        // For radial layouts, push label outward from center using _radialAngle
        if (isRadialLabel) {
          const angle = node._radialAngle;
          const labelDist = r + 14;
          const lx = Math.cos(angle) * labelDist;
          const ly = Math.sin(angle) * labelDist;
          // Determine text anchor based on angle quadrant
          const anchor = Math.abs(Math.cos(angle)) < 0.3 ? 'middle' : Math.cos(angle) > 0 ? 'start' : 'end';
          return (
            <text
              textAnchor={anchor}
              fill="var(--text-secondary, #a0a0a0)"
              fontSize={fs}
              fontWeight={600}
              fontFamily="var(--font-primary, sans-serif)"
              style={{ pointerEvents: 'none' }}
            >
              {allLines.map((line, i) => (
                <tspan
                  key={i}
                  x={lx}
                  y={ly + i * (fs + 1)}
                  opacity={line.dim ? 0.6 : 1}
                  fontSize={line.dim ? fs - 1 : fs}
                >{line.text}</tspan>
              ))}
            </text>
          );
        }

        return (
          <text
            textAnchor="middle"
            fill={isHubHeading ? (node.color || '#e2e8f0') : 'var(--text-secondary, #a0a0a0)'}
            fontSize={fs}
            fontWeight={isHubHeading ? 800 : 600}
            letterSpacing={isHubHeading ? 0.3 : undefined}
            fontFamily="var(--font-primary, sans-serif)"
            style={{ pointerEvents: 'none' }}
          >
            {allLines.map((line, i) => (
              <tspan
                key={i}
                x="0"
                y={baseY + i * (fs + 2)}
                opacity={line.dim ? 0.6 : 1}
                fontSize={line.dim ? fs - 1 : fs}
              >{line.text}</tspan>
            ))}
          </text>
        );
      })()}
    </g>
  );
}

function hexPoints(r) {
  const pts = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 6;
    pts.push(`${Math.cos(angle) * r},${Math.sin(angle) * r}`);
  }
  return pts.join(' ');
}


// Word-wrap the FULL text over as many lines as needed — NEVER truncates (used
// by Drug Lab nodes that carry node.no_truncate, e.g. long vaccine names).
function wrapFull(text, perLine = 18) {
  if (!text) return [''];
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = '';
  for (const w of words) {
    if (!cur) cur = w;
    else if ((cur + ' ' + w).length <= perLine) cur += ' ' + w;
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [String(text)];
}

function splitLabel(text, maxPerLine = 20) {
  if (!text) return [''];
  if (text.length <= maxPerLine) return [text];
  // Split near middle at a word boundary
  const mid = Math.floor(text.length / 2);
  let splitIdx = text.lastIndexOf(' ', mid + 5);
  if (splitIdx <= 3) splitIdx = text.indexOf(' ', mid - 5);
  if (splitIdx <= 0) return [text]; // no good break point
  const line1 = text.slice(0, splitIdx);
  const line2 = text.slice(splitIdx + 1);
  return [line1, line2.length > maxPerLine + 5 ? line2.slice(0, maxPerLine + 2) + '\u2026' : line2];
}

// Grid-based positioning: uses node.grid_x / grid_y for explicit 2D layout
function computeGridPositions(nodes, gridCols, gridRows, containerW, containerH) {
  const N = nodes.length;
  if (N === 0) return { positions: {}, totalW: containerW, totalH: containerH };

  const positions = {};
  const x0 = PAD_L, y0 = PAD_T;
  const x1 = containerW - PAD_R, y1 = containerH - PAD_B;
  const areaW = x1 - x0, areaH = y1 - y0;

  // Pin _bottomRight nodes to bottom-right corner
  const pinnedNodes = nodes.filter(n => n._bottomRight);
  const gridNodes = nodes.filter(n => !n._bottomRight);
  pinnedNodes.forEach(n => {
    positions[n.id] = { x: containerW - 60, y: containerH - PAD_B - 30 };
  });

  // Compute actual min/max from node data to handle negative grid_y values
  const gxVals = gridNodes.map(n => n.grid_x ?? 0);
  const gyVals = gridNodes.map(n => n.grid_y ?? 0);
  const minGx = Math.min(...gxVals), maxGx = Math.max(...gxVals);
  const minGy = Math.min(...gyVals), maxGy = Math.max(...gyVals);
  const rawRangeX = maxGx - minGx;
  const rawRangeY = maxGy - minGy;
  const rangeX = rawRangeX || 1;
  const rangeY = rawRangeY || 1;

  const cellW = areaW / rangeX;
  const cellH = areaH / rangeY;

  // Center when all nodes share the same grid coordinate on an axis
  const centerX = x0 + areaW / 2;
  const centerY = y0 + areaH / 2;

  for (const node of gridNodes) {
    const gx = node.grid_x ?? 0;
    const gy = node.grid_y ?? 0;

    // Normalize to 0..1 range then map to pixel area
    const baseX = rawRangeX === 0 ? centerX : x0 + ((gx - minGx) / rangeX) * areaW;
    const baseY = rawRangeY === 0 ? centerY : y0 + ((gy - minGy) / rangeY) * areaH;

    // Small seeded jitter for organic feel
    const jitterX = (seededRandom(node.id + 'gx') - 0.5) * Math.min(cellW, 30) * 0.3;
    const jitterY = (seededRandom(node.id + 'gy') - 0.5) * Math.min(cellH, 20) * 0.3;

    positions[node.id] = {
      x: Math.max(x0, Math.min(x1, baseX + jitterX)),
      y: Math.max(y0, Math.min(y1, baseY + jitterY)),
    };
  }

  return { positions, totalW: containerW, totalH: containerH };
}

function computeRadialPositions(nodes, containerW, containerH) {
  const positions = {};
  const cx = containerW / 2;
  // Tray-aware inner box: match diagonal/grid so the ellipse cannot
  // cross the top pill nav or the bottom action trays.
  const innerH = Math.max(1, containerH - PAD_T - PAD_B);
  const cy = PAD_T + innerH / 2;

  const centerNode = nodes.find(n => n._center);
  if (centerNode) {
    positions[centerNode.id] = { x: cx, y: cy };
  }

  nodes.filter(n => n._bottomRight).forEach(n => {
    positions[n.id] = { x: containerW - 60, y: containerH - PAD_B - 30 };
  });

  // Separate moon nodes (_orbitParent set) from ring nodes
  const moonNodes = nodes.filter(n => n._orbitParent);
  const ringCandidates = nodes.filter(n => !n._center && !n._bottomRight && !n._orbitParent);

  const ringGroups = {};
  ringCandidates.forEach(n => {
    const ring = n._ring ?? 1;
    if (!ringGroups[ring]) ringGroups[ring] = [];
    ringGroups[ring].push(n);
  });

  const ringKeys = Object.keys(ringGroups).map(Number).sort((a, b) => a - b);
  const isSolarSystem = ringCandidates.some(n => n.type === 'cram_disease_treatment');
  const hPad = isSolarSystem ? 30 : 70;
  const vPad = isSolarSystem ? 10 : 25;
  const maxRx = (containerW / 2) - hPad;
  // For solar system: flatten ry to 55% of rx for 3D perspective tilt
  const maxRy = isSolarSystem
    ? Math.min((innerH / 2) - vPad, maxRx * 0.55)
    : (innerH / 2) - vPad;

  // Tag planet orbit params for animated rendering
  const tagPlanetOrbit = (node, i, total, rx, ry) => {
    if (node.type === 'cram_disease_treatment') {
      node._planetOrbit = true;
      node._planetIdx = i;
      node._planetTotal = total;
      node._planetRx = rx;
      node._planetRy = ry;
      node._planetCenter = { x: cx, y: cy };
    }
  };

  if (ringKeys.length === 1) {
    const ringNodes = ringGroups[ringKeys[0]];
    const hasSolarPlanets = ringNodes.some(n => n.type === 'cram_disease_treatment');

    if (hasSolarPlanets && ringNodes.length > 10) {
      // Split into inner/outer rings
      const half = Math.ceil(ringNodes.length / 2);
      const innerNodes = ringNodes.slice(0, half);
      const outerNodes = ringNodes.slice(half);
      const innerRx = maxRx * 0.45, innerRy = maxRy * 0.45;
      const outerRx = maxRx * 0.95, outerRy = maxRy * 0.95;
      const startAngle = -Math.PI / 2;
      innerNodes.forEach((node, i) => {
        const angle = startAngle + (2 * Math.PI * i) / innerNodes.length;
        positions[node.id] = { x: cx + innerRx * Math.cos(angle), y: cy + innerRy * Math.sin(angle) };
        node._radialAngle = angle;
        tagPlanetOrbit(node, i, innerNodes.length, innerRx, innerRy);
      });
      outerNodes.forEach((node, i) => {
        const angle = startAngle + (Math.PI / outerNodes.length) + (2 * Math.PI * i) / outerNodes.length;
        positions[node.id] = { x: cx + outerRx * Math.cos(angle), y: cy + outerRy * Math.sin(angle) };
        node._radialAngle = angle;
        tagPlanetOrbit(node, i, outerNodes.length, outerRx, outerRy);
      });
    } else {
      const rx = maxRx * (hasSolarPlanets ? 0.92 : 0.80);
      const ry = maxRy * (hasSolarPlanets ? 0.92 : 0.80);
      const startAngle = -Math.PI / 2;
      ringNodes.forEach((node, i) => {
        const angle = startAngle + (2 * Math.PI * i) / ringNodes.length;
        positions[node.id] = { x: cx + rx * Math.cos(angle), y: cy + ry * Math.sin(angle) };
        node._radialAngle = angle;
        tagPlanetOrbit(node, i, ringNodes.length, rx, ry);
      });
    }
  } else {
    const ringRadii = { 1: 0.35, 2: 0.62, 3: 1.0 };
    const ringOffsets = { 1: Math.PI / 6, 2: 0, 3: Math.PI / 4 };
    ringKeys.forEach((ringNum) => {
      const radiusFrac = ringRadii[ringNum] ?? ((ringNum) / (ringKeys.length) * 0.9 + 0.1);
      const ringNodes = ringGroups[ringNum];
      const startAngle = -Math.PI / 2 + (ringOffsets[ringNum] ?? 0);
      ringNodes.forEach((node, i) => {
        // Guarded per-node overrides (additive — only apply when explicitly set,
        // so all existing radial maps are unaffected): `_ringAngle` pins an
        // absolute angle (radians) instead of even-spacing, `_ringFrac` pins a
        // per-node radius fraction. Lets a caller place specific ring nodes
        // symmetrically (e.g. a mirrored left/right pair, which even-spacing of
        // 2 antipodal nodes cannot express).
        const angle = (typeof node._ringAngle === 'number')
          ? node._ringAngle
          : startAngle + (2 * Math.PI * i) / ringNodes.length;
        const nodeFrac = (typeof node._ringFrac === 'number') ? node._ringFrac : radiusFrac;
        const nrx = maxRx * nodeFrac;
        const nry = maxRy * nodeFrac;
        positions[node.id] = { x: cx + nrx * Math.cos(angle), y: cy + nry * Math.sin(angle) };
        node._radialAngle = angle;
        tagPlanetOrbit(node, i, ringNodes.length, nrx, nry);
      });
    });
  }

  // Position moon nodes in small orbits around their parent planet
  const moonsByParent = {};
  moonNodes.forEach(n => {
    const pid = n._orbitParent;
    if (!moonsByParent[pid]) moonsByParent[pid] = [];
    moonsByParent[pid].push(n);
  });
  const MOON_ORBIT_R = Math.min(maxRx, maxRy) * 0.28;
  Object.entries(moonsByParent).forEach(([parentId, moons]) => {
    const parentPos = positions[parentId];
    if (!parentPos) {
      moons.forEach((moon, i) => {
        const angle = -Math.PI / 2 + (2 * Math.PI * i) / moons.length;
        positions[moon.id] = { x: cx + maxRx * 0.55 * Math.cos(angle), y: cy + maxRy * 0.55 * Math.sin(angle) };
        moon._radialAngle = angle;
      });
      return;
    }
    moons.forEach((moon, i) => {
      const angle = (2 * Math.PI * i) / moons.length - Math.PI / 2;
      positions[moon.id] = {
        x: parentPos.x + MOON_ORBIT_R * Math.cos(angle),
        y: parentPos.y + MOON_ORBIT_R * Math.sin(angle),
      };
      moon._radialAngle = angle;
      moon._moonOrbitR = MOON_ORBIT_R;
      moon._moonIdx = i;
      moon._moonTotal = moons.length;
      moon._parentPos = parentPos;
    });
  });

  return { positions, totalW: containerW, totalH: containerH };
}

function ProgressMap({ mapData, onNodeClick, onNodeHover, deckCreatureMap, height, glowNodeTypes, dlc, activeNodeId, forceClickable = false, emptyLabel = 'Progress Map', emptySub = 'Map data loading...', surfaceOverlay = null }) {
  const [hovered, setHovered] = useState(null);
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: 800, h: 400 });
  // Canonical DLC color context for satellite node tinting (rx_detail, safety,
  // clinical_round). Use explicit `dlc` prop, fall back to mapData.dlc, then '' (no-op).
  const mapDlc = dlc || mapData?.dlc || '';

  // Measure container synchronously before paint to avoid 1-frame layout flash
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) setContainerSize({ w: rect.width, h: rect.height });
  }, []);

  // Observe ongoing size changes (after paint is fine for resize events)
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height: h } = entries[0].contentRect;
      if (width > 0 && h > 0) setContainerSize({ w: width, h });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Dynamic CRAM node scale: fewer satellites = bigger, more = tighter
  const cramScale = useMemo(() => {
    if (!mapData?.nodes) return 1;
    const cramCount = mapData.nodes.filter(n => n.type?.startsWith('cram_')).length;
    return cramCount > 0 ? cramNodeScale(cramCount) : 1;
  }, [mapData]);

  const { nodes, edges, nodesById, positions, totalW, totalH } = useMemo(() => {
    if (!mapData || !mapData.nodes || mapData.nodes.length === 0) {
      return { nodes: [], edges: [], nodesById: {}, positions: {}, totalW: 0, totalH: 0 };
    }

    const n = mapData.nodes;
    const e = mapData.edges || [];
    const byId = {};
    n.forEach(node => { byId[node.id] = node; });

    // Use radial/grid layout when flagged, otherwise default diagonal scatter
    let pos, tw, th;
    if (mapData.layout === 'radial') {
      const allFixed = n.every(node => typeof node.x === 'number' && typeof node.y === 'number');
      if (allFixed) {
        pos = {};
        n.forEach((node) => {
          pos[node.id] = { x: node.x, y: node.y };
        });
        const maxX = Math.max(...n.map(node => node.x));
        const maxY = Math.max(...n.map(node => node.y));
        tw = mapData.fixedWidth || Math.max(containerSize.w, maxX + 80);
        th = mapData.fixedHeight || Math.max(containerSize.h, maxY + 120);
      } else {
        ({ positions: pos, totalW: tw, totalH: th } = computeRadialPositions(n, containerSize.w, containerSize.h));
      }
    } else if (mapData.layout === 'grid') {
      const cols = mapData.grid_cols || (Math.max(...n.map(nd => nd.grid_x)) + 1);
      const rows = mapData.grid_rows || (Math.max(...n.map(nd => nd.grid_y)) + 1);
      ({ positions: pos, totalW: tw, totalH: th } = computeGridPositions(n, cols, rows, containerSize.w, containerSize.h));
    } else {
      const cols = mapData.grid_cols || (Math.max(...n.map(nd => nd.grid_x)) + 1);
      const rows = mapData.grid_rows || (Math.max(...n.map(nd => nd.grid_y)) + 1);
      ({ positions: pos, totalW: tw, totalH: th } = computePositions(n, cols, rows, containerSize.w, containerSize.h));
    }
    return { nodes: n, edges: e, nodesById: byId, positions: pos, totalW: tw, totalH: th };
  }, [mapData, containerSize]);

  if (!mapData || nodes.length === 0) {
    return (
      <div ref={containerRef} className="pmap-empty" style={height ? { height } : undefined}>
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
          <line x1="8" y1="2" x2="8" y2="18" />
          <line x1="16" y1="6" x2="16" y2="22" />
        </svg>
        <span className="pmap-empty-label">{emptyLabel}</span>
        <span className="pmap-empty-sub">{emptySub}</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="pmap-container" style={height ? { height, maxHeight: height } : undefined}>
      <svg
        className="pmap-svg"
        viewBox={`0 0 ${totalW} ${totalH}`}
        style={{ width: '100%', height: '100%' }}
      >
        {/* Subtle background dots + portal glow filter */}
        <defs>
          <pattern id="pmap-dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
            <circle cx="10" cy="10" r="0.5" fill="rgba(255,255,255,0.04)" />
          </pattern>
          <filter id="portal-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <rect width={totalW} height={totalH} fill="url(#pmap-dots)" />

        {/* Grouping REGIONS — optional tinted panels drawn behind everything, in
            the same coordinate space as the nodes. `mapData.regions` is absent on
            every map that does not ask for them, so this renders nothing unless a
            caller opts in (today: the Drug Lab's 'All Systems' compass, where each
            therapeutic system gets one panel so membership reads by common region
            rather than by proximity alone). Non-interactive by construction — the
            heading node inside the panel is what navigates. */}
        {Array.isArray(mapData?.regions) && mapData.regions.map((rg) => (
          <g key={`region-${rg.id}`} style={{ pointerEvents: 'none' }}>
            {/* A region is a disc when it carries a radius, else a rounded rect. */}
            {typeof rg.r === 'number' ? (
              <circle
                data-region-id={rg.id}
                cx={rg.cx} cy={rg.cy} r={rg.r}
                fill={rg.color || '#cbd5e1'} fillOpacity={0.05}
                stroke={rg.color || '#cbd5e1'} strokeOpacity={0.28} strokeWidth={1}
              />
            ) : (
              <rect
                data-region-id={rg.id}
                x={rg.x} y={rg.y} width={rg.w} height={rg.h} rx={14} ry={14}
                fill={rg.color || '#cbd5e1'} fillOpacity={0.05}
                stroke={rg.color || '#cbd5e1'} strokeOpacity={0.28} strokeWidth={1}
              />
            )}
          </g>
        ))}

        {/* Edges first (behind nodes) */}
        {edges.map((edge, i) => (
          <MapEdge
            key={`${edge.from}-${edge.to}-${i}`}
            edge={{ ...edge, _idx: i }}
            positions={positions}
            nodesById={nodesById}
            mapDlc={mapDlc}
          />
        ))}

        {/* Caller-owned decoration painted in SURFACE coordinates, above the edges
            and below the nodes. Opt-in and non-interactive by construction: absent
            the prop this renders nothing, and pointer events stay off so a mark can
            sit over the edges converging on it without swallowing a node's click.
            Consumed today by the Drug Lab, which paints the Debono mark at the
            centre of its compass hub where the 'All Systems' node used to be. */}
        {surfaceOverlay && (
          <g style={{ pointerEvents: 'none' }}>{surfaceOverlay}</g>
        )}

        {/* Orbit track ellipses — tilted 3D rings with depth gradient */}
        {(() => {
          const seen = new Set();
          const tracks = [];
          nodes.filter(n => n._planetOrbit).forEach(n => {
            const key = `${n._planetRx}-${n._planetRy}`;
            if (seen.has(key)) return;
            seen.add(key);
            const gradId = `orbit-depth-${key.replace(/\./g, '_')}`;
            tracks.push(
              <React.Fragment key={`orbit-track-${key}`}>
                {/* Gradient: top half (back) dimmer, bottom half (front) brighter */}
                <defs>
                  <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(249, 115, 22, 0.02)" />
                    <stop offset="50%" stopColor="rgba(249, 115, 22, 0.04)" />
                    <stop offset="100%" stopColor="rgba(249, 115, 22, 0.10)" />
                  </linearGradient>
                </defs>
                <ellipse
                  cx={n._planetCenter.x} cy={n._planetCenter.y}
                  rx={n._planetRx} ry={n._planetRy}
                  fill="none" stroke={`url(#${gradId})`} strokeWidth={1}
                />
              </React.Fragment>
            );
          });
          return tracks;
        })()}

        {/* Nodes — 3D orbital layout: back-half planets → hub → front-half planets */}
        {(() => {
          const moonsByPlanet = {};
          const planetNodes = [];
          const staticNodes = [];
          const hubNodes = [];  // octagon/hub rendered on top for 3D depth
          // `hidden` nodes contribute their POSITION but paint nothing: an edge that
          // terminates on one still draws, which is the whole point (the Drug Lab's
          // compass keeps its centre node so its eight spokes have somewhere to land,
          // while the Debono mark is painted there instead). Filtered here, at paint
          // time only — `positions` and `nodesById` above are built from the full
          // `nodes`, so edge resolution is untouched.
          nodes.filter(n => !n.hidden).forEach(n => {
            if (n._planetOrbit && n._planetCenter) planetNodes.push(n);
            else if (n._moonOrbitR && n._orbitParent) {
              if (!moonsByPlanet[n._orbitParent]) moonsByPlanet[n._orbitParent] = [];
              moonsByPlanet[n._orbitParent].push(n);
            }
            else if (CRAM_FULL_SIZE_TYPES.has(n.type)) hubNodes.push(n);
            else staticNodes.push(n);
          });

          const baseMoonR = planetNodes.length > 0 && moonsByPlanet[planetNodes[0]?.id]?.[0]?._moonOrbitR
            ? moonsByPlanet[planetNodes[0].id][0]._moonOrbitR : 40;
          const MOON_R = Math.max(16, baseMoonR * cramScale);

          // Split planets into back-half (top of ellipse, behind hub) and front-half
          // based on their starting angle. Top = y < center = "behind", bottom = "in front".
          const backPlanets = [];
          const frontPlanets = [];
          planetNodes.forEach(planet => {
            const startAngle = -Math.PI / 2 + (2 * Math.PI * planet._planetIdx / planet._planetTotal);
            // Top half of orbit (angles pointing up) = behind the hub
            const yFrac = Math.sin(startAngle);  // -1 = top, +1 = bottom
            if (yFrac <= 0.1) backPlanets.push(planet);
            else frontPlanets.push(planet);
          });

          // Render a planet with animated depth: scale + opacity cycle with orbital position.
          // Motion path starts at (rx,0) = right side, traces clockwise:
          //   t=0 right, t=0.25 bottom(front), t=0.5 left, t=0.75 top(back)
          // Depth: front(bottom)=full, back(top)=reduced
          const renderPlanet = (planet, isBack) => {
            const { x: pcx, y: pcy } = planet._planetCenter;
            const prx = planet._planetRx;
            const pry = planet._planetRy;
            const dur = 90;
            const startDelay = -(dur * planet._planetIdx / planet._planetTotal);
            const ellipsePath = `M ${prx},0 A ${prx},${pry} 0 1,1 ${-prx},0 A ${prx},${pry} 0 1,1 ${prx},0`;
            const moons = moonsByPlanet[planet.id] || [];
            // Depth keyframes synced with orbital motion:
            // right(0) → bottom/front(0.25) → left(0.5) → top/back(0.75) → right(1)
            const scaleKeys = '0.875;1;0.875;0.75;0.875';
            const opacityKeys = '0.75;1;0.75;0.5;0.75';
            const keyTimes = '0;0.25;0.5;0.75;1';
            const easeSplines = '0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1';

            return (
              <g key={planet.id} transform={`translate(${pcx},${pcy})`}>
                <g>
                  <animate attributeName="opacity" dur={`${dur}s`} begin={`${startDelay}s`}
                    repeatCount="indefinite" values={opacityKeys} keyTimes={keyTimes}
                    calcMode="spline" keySplines={easeSplines} />
                  <animateMotion dur={`${dur}s`} begin={`${startDelay}s`} repeatCount="indefinite" path={ellipsePath} rotate="0" />
                  <g>
                    <animateTransform attributeName="transform" type="scale" dur={`${dur}s`}
                      begin={`${startDelay}s`} repeatCount="indefinite" values={scaleKeys}
                      keyTimes={keyTimes} calcMode="spline" keySplines={easeSplines} />
                    <MapNode node={planet} pos={{ x: 0, y: 0 }} onNodeClick={onNodeClick} onNodeHover={onNodeHover} hovered={hovered} onHover={setHovered} cramScale={cramScale} glowNodeTypes={glowNodeTypes} mapDlc={mapDlc} activeNodeId={activeNodeId} forceClickable={forceClickable} />
                    {moons.length > 0 && (() => {
                      const moonRx = MOON_R;
                      const moonRy = MOON_R * 0.5;
                      return (
                        <ellipse rx={moonRx} ry={moonRy}
                          fill="none" stroke="rgba(59, 130, 246, 0.06)" strokeWidth={0.5} />
                      );
                    })()}
                    {moons.map(moon => {
                      const moonRx = MOON_R;
                      const moonRy = MOON_R * 0.5;
                      const moonDur = 18 + (moon._moonIdx || 0) * 2;
                      const moonStartDelay = -(moonDur * moon._moonIdx / moon._moonTotal);
                      const moonPath = `M ${moonRx},0 A ${moonRx},${moonRy} 0 1,1 ${-moonRx},0 A ${moonRx},${moonRy} 0 1,1 ${moonRx},0`;
                      return (
                        <g key={moon.id}>
                          <animateMotion dur={`${moonDur}s`} begin={`${moonStartDelay}s`} repeatCount="indefinite" path={moonPath} rotate="0" />
                          <MapNode node={moon} pos={{ x: 0, y: 0 }} onNodeClick={onNodeClick} onNodeHover={onNodeHover} hovered={hovered} onHover={setHovered} cramScale={cramScale} glowNodeTypes={glowNodeTypes} mapDlc={mapDlc} activeNodeId={activeNodeId} forceClickable={forceClickable} />
                        </g>
                      );
                    })}
                  </g>
                </g>
              </g>
            );
          };

          return (
            <>
              {/* Layer 1: non-hub static nodes */}
              {staticNodes.map(node => (
                <MapNode key={node.id} node={node} pos={positions[node.id]} onNodeClick={onNodeClick} onNodeHover={onNodeHover} hovered={hovered} onHover={setHovered} cramScale={cramScale} glowNodeTypes={glowNodeTypes} mapDlc={mapDlc} activeNodeId={activeNodeId} forceClickable={forceClickable} />
              ))}

              {/* Layer 2: back-half planets (behind the hub — rendered first for z-order) */}
              {backPlanets.map(planet => renderPlanet(planet, true))}

              {/* Layer 3: hub/octagon nodes */}
              {hubNodes.map(node => (
                <MapNode key={node.id} node={node} pos={positions[node.id]} onNodeClick={onNodeClick} onNodeHover={onNodeHover} hovered={hovered} onHover={setHovered} cramScale={cramScale} glowNodeTypes={glowNodeTypes} mapDlc={mapDlc} activeNodeId={activeNodeId} forceClickable={forceClickable} />
              ))}

              {/* Layer 4: front-half planets (in front of the hub) */}
              {frontPlanets.map(planet => renderPlanet(planet, false))}
            </>
          );
        })()}
      </svg>
    </div>
  );
}

export default React.memo(ProgressMap);
